// ── Tlhakiso Stokvel — web module (practice/admin surface) ──
//
// NON-CUSTODIAL transparency layer for savings groups. The platform never holds or
// moves money: the group's funds stay in the group's OWN bank account. We record
// contributions, loans (with interest), repayments and payouts, give every member
// full visibility, and reconcile money-in against the (read-only) bank feed by each
// member's unique payment reference. Shares the backend with the native app.
import React, { useState, useEffect, useMemo } from "react";
import { findUserByEmail, joinStokvelByCode, approveJoinRequest, rejectJoinRequest, listJoinRequests, listMyPendingJoins } from "./firebase";
import {
  listStokvelsForUser, createStokvel, loadStokvelData, saveStokvel,
  addStokvelMember, updateStokvelMember, removeStokvelMember, setStokvelJoinCode,
  addContribution, addContributions, voidContribution,
  addPayout, deletePayout, addLoan, updateLoan, addRepayment, deleteRepayment,
  addExpense, deleteExpense, addIncome, deleteIncome, applyImport,
  addClaim, updateClaim, deleteClaim, payClaim,
  addDependant, deleteDependant,
  proposeStokvelChange, approvePendingChange, rejectPendingChange, loadStokvelAuditLog,
  proposeAmendment, confirmAmendment, rejectAmendment,
  saveConstitutionVersion, loadConstitutionHistory, loadMinutes, addMinute, deleteMinute,
  loadStokvelDocs, addStokvelDoc, deleteStokvelDoc,
  loadStokvelBankTransactions, attachReceipts, setVerification,
} from "./stokvelFeed";
import { extractPdfText } from "./pdfText";
import { jsPDF } from "jspdf";
import { CONSTITUTION_TEMPLATES, constitutionTemplate } from "./stokvelTemplates";
import {
  STOKVEL_TYPES, FREQUENCIES, INTEREST_TYPES,
  EXPENSE_CATEGORIES, INCOME_CATEGORIES, expenseCategoryLabel, incomeCategoryLabel,
  money, codeFromName, nextMemberReference, memberSummaries, stokvelTotals,
  movementFeed, loanSummaries, proposeFromBank, normRef, makeJoinCode, bankLabelFrom, memberKey, freqLabel,
  BURIAL_RELATIONSHIPS, relationshipLabel, CLAIM_STATUSES, claimStatusLabel, claimStatusTone,
  coverStatus, DEFAULT_CLAIM_WAITING_DAYS, DEFAULT_COVERED_PER_MEMBER,
  REFERENCE_SCHEMES, generateReference, receiptsOfTxn, verificationState,
} from "./stokvelHelpers";
import { parseStatementFile, classifyStatement, reclassifyRow, rowsToBuckets, IMPORT_TYPES } from "./stokvelImport";
import { uploadReceipt, uploadReceipts, removeReceipt } from "./stokvelReceipts";
import { subscriptionState, startSubscription, cancelSubscription, verifyPaystack, pendingPaystack, clearPendingPaystack } from "./stokvelBilling";
import { BANK_FEED_ENABLED } from "./bankFeed/config";
import { openConnectWidget } from "./bankFeed/stitchClient";
import { loadStokvelBankFeedConfig, syncStokvelBankFeed } from "./bankFeed/sync";

const C = { navy: "#1a1a2e", gold: "#c8a24e", cream: "#faf9f6", line: "#e8e4dc", ink: "#2c2a26", muted: "#9a9488", green: "#3d7a52", red: "#b33" };
const todayIso = () => new Date().toISOString().slice(0, 10);

const card = { background: C.cream, border: "1px solid " + C.line, borderRadius: 10, padding: 16, marginBottom: 14 };
const inp = { width: "100%", boxSizing: "border-box", padding: "9px 11px", borderRadius: 6, border: "1px solid #e0ddd8", fontSize: 13, outline: "none", background: "#fff", marginTop: 4 };
const lbl = { fontSize: 11, color: "#8a8578", fontWeight: 600, display: "block", marginTop: 12 };

// Granular permissions. Chair has everything. A treasurer has only what the chair
// granted via members[uid].perms; a treasurer with no perms set keeps the legacy
// ability to record money. Ordinary members have none (read-only). Changing who is a
// member and their role stays chair-only (enforced by the security rules).
const PERMS = [
  { id: "money", label: "Record money" },
  { id: "approve", label: "Approve new members" },
  { id: "claims", label: "Manage claims" },
  { id: "family", label: "Manage covered family" },
  { id: "settings", label: "Change settings & rules" },
  { id: "governance", label: "Manage constitution & minutes" },
];
const STOKVEL_ROLE_LABELS = { chair: "Chairperson", deputy: "Deputy Chair", secretary: "Secretary", treasurer: "Treasurer", member: "Member" };
// Capability resolver. The chairperson can do everything; a treasurer can always
// record money; everyone else can only do what the chair explicitly granted them via
// their `perms` (e.g. money, approve, settings). Roles (deputy/secretary/…) are labels
// — capability comes from the granted permission, per the group's setup.
function memberCan(stokvel, uid, perm) {
  const m = (stokvel && stokvel.members && stokvel.members[uid]) || null;
  if (!m) return false;
  if (m.role === "chair") return true;
  if (perm === "money" && m.role === "treasurer") return true;
  const p = m.perms || {};
  return p[perm] === true;
}

function Btn({ children, onClick, kind, disabled, style }) {
  const base = { padding: "8px 16px", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: disabled ? "default" : "pointer", border: "1px solid transparent" };
  const kinds = {
    primary: { background: C.navy, color: C.gold, border: "1px solid " + C.navy },
    gold: { background: C.gold, color: C.navy },
    green: { background: C.green, color: "#fff" },
    ghost: { background: "#fff", color: C.navy, border: "1px solid #e0ddd8" },
    danger: { background: "#fff", color: C.red, border: "1px solid #e8cccc" },
  };
  return <button onClick={onClick} disabled={disabled} style={{ ...base, ...(kinds[kind] || kinds.primary), ...(disabled ? { opacity: 0.5 } : {}), ...style }}>{children}</button>;
}

function KPI({ label, value, sub, accent }) {
  return (
    <div style={{ background: accent ? C.navy : "#fff", borderRadius: 10, padding: "13px 16px", border: "1px solid " + (accent ? "#2a2a4a" : C.line) }}>
      <div style={{ fontSize: 10, color: accent ? "#8b8ba0" : C.muted, textTransform: "uppercase", letterSpacing: 1.1, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 19, fontWeight: 700, color: accent ? C.gold : C.navy, marginTop: 3 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: accent ? "#5a5a7a" : "#b0aa9e", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Badge({ children, tone }) {
  const tones = { ok: { bg: "#dff5e1", fg: "#2c6e49" }, warn: { bg: "#fff3c4", fg: "#8a6d1d" }, bad: { bg: "#ffd9d9", fg: "#a33" }, info: { bg: "#e6edf6", fg: "#3d567a" }, gold: { bg: "#f6ecd2", fg: "#8a6d1d" } };
  const t = tones[tone] || tones.info;
  return <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: t.bg, color: t.fg }}>{children}</span>;
}

function Modal({ title, onClose, children, width }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999 }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, padding: "22px 26px", width: width || 480, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.navy }}>{title}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#999" }}>&times;</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Disclaimer() {
  return (
    <div style={{ background: "#fff8e6", border: "1px solid #f0d97f", borderRadius: 8, padding: "8px 12px", fontSize: 11, color: "#7a5b00", marginBottom: 14, lineHeight: 1.5 }}>
      🔒 <strong>Tlhakiso Stokvel is non-custodial.</strong> It does not hold, move, or guarantee any money. Your funds stay in the group's own bank account — this is a record-keeping and transparency tool, not a bank or financial service provider.
    </div>
  );
}

// ───────────────────────────── Create / Add-member modals ─────────────────────────────

function CreateStokvelModal({ onClose, onCreate }) {
  const [fm, setFm] = useState({ name: "", type: "savings", frequency: "monthly", contributionAmount: "", bankName: "", accountName: "", accountNumber: "", branchCode: "", creatorName: "" });
  const u = (k, v) => setFm((s) => ({ ...s, [k]: v }));
  const submit = () => {
    if (!fm.name.trim()) return alert("Enter a group name");
    onCreate({ ...fm, contributionAmount: Number(fm.contributionAmount) || 0 });
  };
  return (
    <Modal title="New stokvel" onClose={onClose} width={520}>
      <label style={lbl}>Group name *</label>
      <input style={inp} value={fm.name} onChange={(e) => u("name", e.target.value)} placeholder="e.g. Masakhane Stokvel" />
      <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Reference code: <strong>{codeFromName(fm.name)}</strong> — members' payment references start with this.</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div><label style={lbl}>Type</label><select style={{ ...inp, cursor: "pointer" }} value={fm.type} onChange={(e) => u("type", e.target.value)}>{STOKVEL_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}</select></div>
        <div><label style={lbl}>Contribution frequency</label><select style={{ ...inp, cursor: "pointer" }} value={fm.frequency} onChange={(e) => u("frequency", e.target.value)}>{FREQUENCIES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}</select></div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div><label style={lbl}>Expected contribution (R)</label><input style={inp} type="number" value={fm.contributionAmount} onChange={(e) => u("contributionAmount", e.target.value)} placeholder="500" /></div>
        <div><label style={lbl}>Your name</label><input style={inp} value={fm.creatorName} onChange={(e) => u("creatorName", e.target.value)} placeholder="Chairperson name" /></div>
      </div>

      <div style={{ fontSize: 12, fontWeight: 700, color: C.navy, marginTop: 16 }}>Group bank account <span style={{ fontWeight: 400, color: C.muted }}>— the group's own account (for reference; no money is held here)</span></div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div><label style={lbl}>Bank</label><input style={inp} value={fm.bankName} onChange={(e) => u("bankName", e.target.value)} placeholder="e.g. Standard Bank" /></div>
        <div><label style={lbl}>Name on account</label><input style={inp} value={fm.accountName} onChange={(e) => u("accountName", e.target.value)} placeholder="Account holder" /></div>
        <div><label style={lbl}>Account number</label><input style={inp} value={fm.accountNumber} onChange={(e) => u("accountNumber", e.target.value)} placeholder="e.g. 10 1234 5678" /></div>
        <div><label style={lbl}>Branch code</label><input style={inp} value={fm.branchCode} onChange={(e) => u("branchCode", e.target.value)} placeholder="e.g. 051001" /></div>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
        <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
        <Btn kind="primary" onClick={submit}>Create stokvel</Btn>
      </div>
    </Modal>
  );
}

function AddMemberModal({ stokvel, onClose, onAdd }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("member");
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState("");
  const [reference, setReference] = useState(() => generateReference(stokvel, stokvel.members || {}, "", null));
  const [refTouched, setRefTouched] = useState(false);
  // Auto-generate the reference from the group's preset scheme as the name is typed,
  // unless the chair has manually edited it (e.g. keeping a migrated member's old ref).
  React.useEffect(() => { if (!refTouched) setReference(generateReference(stokvel, stokvel.members || {}, name, null)); }, [name]); // eslint-disable-line
  const [openingBalance, setOpeningBalance] = useState("");
  const submit = async () => {
    const ref = reference.trim();
    const dn = name.trim();
    if (!dn) return setStatus("Enter the member's name");
    if (!ref) return setStatus("Enter a payment reference");
    if (ref.replace(/[^A-Za-z0-9]/g, "").length < 3) return setStatus("Reference must be at least 3 letters/numbers so payments can auto-match");
    const dupRef = Object.entries(stokvel.members || {}).some(([, m]) => normRef(m.reference) === normRef(ref));
    if (dupRef) return setStatus("Another member already uses that reference — give this member a distinct one");
    if (Object.values(stokvel.members || {}).some((m) => (m.name || "").trim().toLowerCase() === dn.toLowerCase())) {
      if (!window.confirm('A member named "' + dn + '" is already in this group. This will add a SEPARATE second member (not update the existing one). Add anyway?')) return;
    }
    const em = email.trim();
    if (em) {
      // Email supplied → try to link to their Tlhakiso account so they can sign in and view.
      setStatus("Looking up user…");
      const found = await findUserByEmail(em);
      if (found) return onAdd({ uid: found.uid, email: found.email, name: dn, role, phone: phone.trim(), reference: ref, openingBalance: Number(openingBalance) || 0 });
      // No account yet — add as a tracked member, keeping the email on record.
      if (!window.confirm("No Tlhakiso account exists for " + em + " yet.\n\nAdd them as a tracked member for now? Their contributions are recorded and visible to the group; they can get their own login later by joining with your invite code.")) return;
      return onAdd({ email: em, name: dn, role, phone: phone.trim(), reference: ref, openingBalance: Number(openingBalance) || 0 });
    }
    // No email → tracked member (name only). Recorded in the books; no app login.
    onAdd({ name: dn, role, phone: phone.trim(), reference: ref, openingBalance: Number(openingBalance) || 0 });
  };
  return (
    <Modal title="Add member" onClose={onClose}>
      <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>A <strong>name</strong> is all you need — the member is recorded in the group's books straight away. Add an <strong>email</strong> too if you want to link their own Tlhakiso login so they can sign in and view; otherwise they can get a login later via <strong>Invite with code</strong>.</div>
      <label style={lbl}>Name</label><input style={inp} value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" autoFocus />
      <label style={lbl}>Member email (optional)</label><input style={inp} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="member@email.com — links their app login" />
      <label style={lbl}>WhatsApp number (optional)</label><input style={inp} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="082 123 4567 — lets them check their balance on WhatsApp" />
      <label style={lbl}>Role</label><select style={{ ...inp, cursor: "pointer" }} value={role} onChange={(e) => setRole(e.target.value)}><option value="member">Member</option><option value="treasurer">Treasurer</option><option value="deputy">Deputy Chair</option><option value="secretary">Secretary</option></select>
      <label style={lbl}>Payment reference</label>
      <input style={{ ...inp, fontFamily: "monospace", textTransform: "uppercase" }} value={reference} onChange={(e) => { setRefTouched(true); setReference(e.target.value); }} placeholder="Payment reference" />
      <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Auto-generated from your group's <strong>reference format</strong> (set it in Settings). If this member has <strong>already been paying with a reference for years</strong>, just type that one instead — their existing payments then keep matching.</div>
      <label style={lbl}>Opening balance — contributions to date (optional)</label>
      <input style={inp} type="number" value={openingBalance} onChange={(e) => setOpeningBalance(e.target.value)} placeholder="e.g. 14400" />
      <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>For a stokvel that ran before this app: the total this member has <strong>already contributed</strong> over the years. It seeds their running total so history isn’t lost. Leave blank for a brand-new group.</div>
      {status && <div style={{ marginTop: 8, fontSize: 12, color: C.red }}>{status}</div>}
      <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
        <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
        <Btn kind="primary" onClick={submit}>Add member</Btn>
      </div>
    </Modal>
  );
}

// ───────────────────────────── Invite modal ─────────────────────────────
// Chair-only. Generates ONE shareable invite code for the group — two ways in:
//   • App:      {origin}/join/CODE  → member signs up / signs in and is auto-joined
//   • WhatsApp: member texts "JOIN CODE" to the bot → linked as a tracked member
// The code lives on the stokvel doc; the actual join runs server-side (the security
// rules don't let a brand-new member add themselves to the group).
function InviteModal({ stokvel, onClose, onSetCode }) {
  const [code, setCode] = useState(stokvel.joinCode || "");
  const [active, setActive] = useState(!!stokvel.joinCode && stokvel.joinCodeActive !== false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const waNumber = process.env.REACT_APP_STOKVEL_WA_NUMBER || "";
  const link = code ? (window.location.origin + "/join/" + code) : "";

  const apply = async (nextCode, nextActive) => {
    setBusy(true);
    try {
      await onSetCode(nextCode, nextActive);
      setCode(nextCode); setActive(nextActive);
    } catch (e) { alert(e.message || "Could not update the invite."); }
    setBusy(false);
  };
  const copy = (txt, what) => { try { navigator.clipboard.writeText(txt); setCopied(what); setTimeout(() => setCopied(""), 1500); } catch (e) {} };

  return (
    <Modal title="Invite members" onClose={onClose}>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>
        Share <strong>one code</strong> — members join themselves, so you don't add everyone by hand.
      </div>

      {!active ? (
        <div style={{ textAlign: "center", padding: "10px 0 4px" }}>
          <div style={{ fontSize: 13, color: C.navy, marginBottom: 12 }}>Invites are <strong>off</strong>. Turn them on to get a shareable code and link.</div>
          <Btn kind="primary" onClick={() => apply(code || makeJoinCode(), true)} disabled={busy}>{busy ? "Working…" : "Turn on invites"}</Btn>
        </div>
      ) : (
        <div>
          <label style={lbl}>Invite code</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
            <div style={{ flex: 1, fontFamily: "monospace", fontSize: 22, letterSpacing: 3, fontWeight: 700, color: C.navy, background: "#faf7ef", border: "1px solid " + C.gold, borderRadius: 8, padding: "10px 14px", textAlign: "center" }}>{code}</div>
            <Btn kind="ghost" onClick={() => copy(code, "code")} disabled={busy}>{copied === "code" ? "Copied ✓" : "Copy"}</Btn>
          </div>

          <label style={lbl}>App link — for members who want the app</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
            <input readOnly value={link} style={{ ...inp, fontSize: 12, marginTop: 0 }} onFocus={(e) => e.target.select()} />
            <Btn kind="ghost" onClick={() => copy(link, "link")} disabled={busy}>{copied === "link" ? "Copied ✓" : "Copy"}</Btn>
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>They open it, create an account (verify their email), and are added to <strong>{stokvel.name}</strong> automatically.</div>

          <label style={lbl}>WhatsApp — for members who don't want the app</label>
          <div style={{ background: "#eef6f0", border: "1px solid #cfe3d6", borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "#2f5c40", marginTop: 4 }}>
            Ask them to send <strong>JOIN {code}</strong> {waNumber ? <>to <strong>{waNumber}</strong></> : "to the group's WhatsApp number"}. They'll be linked and can check their own balance on WhatsApp — no app, no account.
          </div>
          <Btn kind="ghost" onClick={() => copy("JOIN " + code, "wa")} disabled={busy} style={{ marginTop: 8 }}>{copied === "wa" ? "Copied ✓" : "Copy WhatsApp message"}</Btn>

          <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "space-between", borderTop: "1px solid " + C.line, paddingTop: 14 }}>
            <Btn kind="ghost" onClick={() => apply(code, false)} disabled={busy} style={{ color: C.red }}>Turn off invites</Btn>
            <Btn kind="ghost" onClick={() => apply(makeJoinCode(), true)} disabled={busy}>Generate new code</Btn>
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>Turning invites off — or generating a new code — immediately stops the old code from working.</div>
        </div>
      )}
    </Modal>
  );
}

// ───────────────────────────── Import statement modal ─────────────────────────────
// Upload a PDF/CSV statement → classify money in/out → auto-match deposits to
// members → treasurer confirms. Nothing is written until they click Record.
function ImportStatementModal({ stokvel, data, onClose, onConfirm }) {
  const members = Object.entries(stokvel.members || {});
  const [phase, setPhase] = useState("choose"); // choose | working | review
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [source, setSource] = useState("");
  const [method, setMethod] = useState("");
  const [dragging, setDragging] = useState(false);
  const okType = (f) => f && /\.(pdf|csv|txt)$/i.test(f.name || "");

  const handleFile = async (file) => {
    if (!file) return;
    setError(""); setPhase("working"); setProgress("Reading " + file.name + "…");
    try {
      const parsed = await parseStatementFile(file, setProgress);
      setSource(parsed.source); setMethod(parsed.method);
      setProgress("Sorting " + parsed.rows.length + " transaction(s)…");
      const { rows: classified } = classifyStatement(parsed.rows, stokvel, data);
      setRows(classified);
      setPhase("review");
    } catch (e) {
      setError(e.message || String(e));
      setPhase("choose");
    }
  };

  const patchRow = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? reclassifyRow(r, patch, stokvel, data) : r)));

  const buckets = rowsToBuckets(rows);
  const nInclude = rows.filter((r) => r.include && r.type !== "skip").length;
  const inTotal = rows.filter((r) => r.include && r.dir === "in" && r.type !== "skip").reduce((s, r) => s + r.amount, 0);
  const outTotal = rows.filter((r) => r.include && r.dir === "out" && r.type !== "skip").reduce((s, r) => s + r.amount, 0);

  const rowStyle = { borderTop: "1px solid #eceae4", fontSize: 12 };
  const cell = { padding: "5px 6px", verticalAlign: "top" };
  const sel = { fontSize: 11, padding: "3px 4px", border: "1px solid #e0ddd8", borderRadius: 4, background: "#fff", maxWidth: 130 };

  return (
    <Modal title="Import bank statement" onClose={onClose} width={860}>
      {phase === "choose" && (
        <div>
          <div style={{ fontSize: 12, color: C.ink, lineHeight: 1.6, marginBottom: 12 }}>
            Upload a statement from the group’s <strong>own</strong> bank account. Money in and out is sorted automatically — contributions, loan repayments, interest earned, bank fees, expenses — and you confirm or re-label each line. <strong>Nothing is saved until you confirm.</strong>
            <div style={{ marginTop: 8, background: "#eef6f0", border: "1px solid #cfe3d6", borderRadius: 8, padding: "8px 12px", color: "#2f5c40" }}>
              💡 <strong>CSV is cheapest &amp; most accurate.</strong> Download transaction history as <strong>CSV/Excel</strong> from your banking app where possible. PDF works too — its text is extracted first, then read.
            </div>
            <div style={{ marginTop: 8, background: "#f4f2ed", border: "1px solid " + C.line, borderRadius: 8, padding: "8px 12px", color: C.ink, fontSize: 11.5, lineHeight: 1.6 }}>
              To auto-sort loans, ask members to add a note on the payment reference: <strong>(loan repayment)</strong> when repaying a loan, and mark disbursements <strong>(loan)</strong>. Bank fees and interest are detected by description. You can re-label anything below.
            </div>
          </div>
          <label
            onDragOver={(e) => { e.preventDefault(); if (!dragging) setDragging(true); }}
            onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
            onDrop={(e) => {
              e.preventDefault(); setDragging(false);
              const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
              if (!f) return;
              if (!okType(f)) { setError("Please drop a PDF or CSV/TXT statement file."); return; }
              handleFile(f);
            }}
            style={{ display: "block", border: "2px dashed " + (dragging ? C.navy : C.gold), borderRadius: 10, padding: 24, textAlign: "center", cursor: "pointer", background: dragging ? "#eef6f0" : "#fffdf6", transition: "background 0.15s, border-color 0.15s" }}>
            <input type="file" accept=".pdf,.csv,.txt" style={{ display: "none" }} onChange={(e) => handleFile(e.target.files && e.target.files[0])} />
            <div style={{ fontSize: 28 }}>⬆</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.navy, marginTop: 6 }}>{dragging ? "Drop the statement to upload" : "Drag a statement here, or click to choose"}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>PDF or CSV · FNB, Capitec, Standard Bank, Absa, Nedbank &amp; more</div>
          </label>
          {error && <div style={{ marginTop: 12, background: "#fff4f4", color: C.red, fontSize: 12, padding: "8px 12px", borderRadius: 6, border: "1px solid #f0c0c0" }}>{error}</div>}
        </div>
      )}

      {phase === "working" && (
        <div style={{ padding: "30px 10px", textAlign: "center" }}>
          <div style={{ fontSize: 26, marginBottom: 10 }}>⏳</div>
          <div style={{ fontSize: 13, color: C.navy, fontWeight: 600 }}>{progress}</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Large PDF statements can take up to a minute.</div>
        </div>
      )}

      {phase === "review" && (
        <div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
            From <strong>{source}</strong> — {rows.length} transaction(s).{" "}
            {method === "csv" && <Badge tone="ok">CSV · no AI cost</Badge>}
            {method === "pdf-text" && <Badge tone="ok">PDF read for free</Badge>}
            {method === "pdf-ai" && <Badge tone="info">PDF · AI-read</Badge>}
            <div style={{ marginTop: 4 }}>Check each line’s <strong>type</strong> and member. Duplicates already in the books are unticked automatically.</div>
          </div>

          <div style={{ ...card, padding: 0, overflow: "auto", maxHeight: "52vh" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ color: C.muted, textAlign: "left", fontSize: 11, position: "sticky", top: 0, background: "#f4f2ed" }}>
                <th style={cell}></th><th style={cell}>Date</th><th style={cell}>Description</th><th style={cell}>Type</th><th style={cell}>Member / category</th><th style={{ ...cell, textAlign: "right" }}>Amount</th>
              </tr></thead>
              <tbody>
                {rows.map((r, i) => {
                  const typeOpts = IMPORT_TYPES.filter((t) => t.dir === r.dir || t.dir === "any");
                  const typeDef = IMPORT_TYPES.find((t) => t.id === r.type);
                  return (
                    <tr key={i} style={{ ...rowStyle, opacity: r.include ? 1 : 0.5 }}>
                      <td style={cell}><input type="checkbox" checked={r.include} disabled={r.type === "skip"} onChange={(e) => patchRow(i, { include: e.target.checked })} /></td>
                      <td style={{ ...cell, whiteSpace: "nowrap" }}>{r.date}</td>
                      <td style={{ ...cell, color: C.muted, maxWidth: 220 }}>{r.description}{r.dup && <div><Badge tone="warn">already recorded</Badge></div>}</td>
                      <td style={cell}>
                        <select value={r.type} onChange={(e) => patchRow(i, { type: e.target.value })} style={sel}>
                          {typeOpts.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                        </select>
                      </td>
                      <td style={cell}>
                        {typeDef && typeDef.member ? (
                          <select value={r.memberKey} onChange={(e) => patchRow(i, { memberKey: e.target.value })} style={sel}>
                            <option value="">— pick member —</option>
                            {members.map(([k, m]) => <option key={k} value={k}>{m.name} ({m.reference})</option>)}
                          </select>
                        ) : r.type === "expense" ? (
                          <select value={r.category} onChange={(e) => patchRow(i, { category: e.target.value })} style={sel}>
                            {EXPENSE_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                          </select>
                        ) : r.type === "income" ? (
                          <select value={r.category} onChange={(e) => patchRow(i, { category: e.target.value })} style={sel}>
                            {INCOME_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                          </select>
                        ) : <span style={{ color: C.muted, fontSize: 11 }}>—</span>}
                        {r.type === "repayment" && !r.loanId && <div style={{ fontSize: 10, color: C.red }}>no active loan — will record as contribution</div>}
                      </td>
                      <td style={{ ...cell, textAlign: "right", fontWeight: 600, color: r.dir === "in" ? C.green : C.red }}>{r.dir === "in" ? "" : "−"}{money(r.amount)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, flexWrap: "wrap", gap: 10 }}>
            <div style={{ fontSize: 12, color: C.ink }}>
              Recording <strong>{nInclude}</strong> item(s): <span style={{ color: C.green, fontWeight: 600 }}>+{money(inTotal)} in</span>, <span style={{ color: C.red, fontWeight: 600 }}>−{money(outTotal)} out</span>.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
              <Btn kind="green" disabled={!nInclude} onClick={() => onConfirm(buckets)}>Record {nInclude} item(s)</Btn>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ───────────────────────────── Tabs ─────────────────────────────

function OverviewTab({ data, bankTxns, canRecord, onReconcile, onImport }) {
  const { stokvel, contributions, payouts, loans, repayments, expenses, income } = data;
  const members = stokvel.members || {};
  // Migration: seed group cash + lifetime contributions from any opening balances
  // set for a stokvel that existed before the app (0 when none → unchanged behaviour).
  const openingContrib = Object.values(members).reduce((s, m) => s + (Number(m.openingBalance) || 0), 0);
  const t = stokvelTotals(contributions, payouts, loans, repayments, undefined, { cash: stokvel.openingCash, contribTotal: openingContrib }, expenses, income);
  const feed = movementFeed(contributions, payouts, loans, repayments, 12, expenses, income);
  const proposals = useMemo(() => proposeFromBank(bankTxns, stokvel, contributions), [bankTxns, stokvel, contributions]);
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,minmax(0,1fr))", gap: 10, marginBottom: 16 }}>
        <KPI label="Cash position" value={money(t.cash)} sub={t.openingCash ? "incl. " + money(t.openingCash) + " opening" : "Recorded in − out"} accent />
        <KPI label="Total contributions" value={money(t.totalIn)} sub={t.openingContrib ? t.contributionCount + " payments + " + money(t.openingContrib) + " b/f" : t.contributionCount + " payments"} />
        <KPI label="Outstanding loans" value={money(t.outstandingLoans)} sub={t.activeLoanCount + " active"} />
        <KPI label="Group worth" value={money(t.worth)} sub="Cash + loans out" />
        <KPI label="Members" value={Object.keys(members).length} sub={stokvel.code} />
      </div>

      {(t.incomeIn > 0 || t.expensesOut > 0) && (
        <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
          {t.incomeIn > 0 && <div style={{ flex: 1, minWidth: 180, background: "#eef6f0", border: "1px solid #cfe3d6", borderRadius: 8, padding: "10px 14px" }}><div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>Interest &amp; income earned</div><div style={{ fontSize: 16, fontWeight: 700, color: C.green }}>+{money(t.incomeIn)}</div><div style={{ fontSize: 10, color: C.muted }}>{t.incomeCount} entr{t.incomeCount === 1 ? "y" : "ies"}</div></div>}
          {t.expensesOut > 0 && <div style={{ flex: 1, minWidth: 180, background: "#fbf0ee", border: "1px solid #e8ccc6", borderRadius: 8, padding: "10px 14px" }}><div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>Fees &amp; expenses</div><div style={{ fontSize: 16, fontWeight: 700, color: C.red }}>−{money(t.expensesOut)}</div><div style={{ fontSize: 10, color: C.muted }}>{t.expenseCount} entr{t.expenseCount === 1 ? "y" : "ies"}</div></div>}
        </div>
      )}

      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>🏦 Bank reconciliation</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Badge tone={bankTxns.length ? "ok" : "warn"}>{bankTxns.length ? bankTxns.length + " transactions synced" : "bank not linked yet"}</Badge>
            {canRecord && <Btn kind="gold" onClick={onImport}>⬆ Import statement</Btn>}
          </div>
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Upload a PDF or CSV statement from the group’s own bank account — deposits auto-match to members by their reference for you to confirm. No money moves.</div>
        {bankTxns.length === 0 ? (
          <div style={{ fontSize: 12, color: C.ink, marginTop: 8, lineHeight: 1.6 }}>
            Link the group's <strong>own</strong> bank account (read-only) and contributions auto-match to members by their payment reference. Until then, record contributions manually — the references are ready:
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
              {Object.values(members).map((m, i) => <span key={i} style={{ fontSize: 11, background: "#fff", border: "1px solid " + C.line, borderRadius: 6, padding: "3px 8px" }}>{m.name}: <strong>{m.reference}</strong></span>)}
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: C.ink, marginTop: 8 }}>
            {proposals.length ? <>{proposals.length} bank credit(s) match a member reference and aren't recorded yet. {canRecord && <Btn kind="green" style={{ marginLeft: 8 }} onClick={() => onReconcile(proposals)}>Reconcile {proposals.length} now</Btn>}</> : "All bank credits are reconciled. ✓"}
          </div>
        )}
      </div>

      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 8 }}>Movement of money</div>
        {feed.length === 0 ? <div style={{ fontSize: 12, color: C.muted }}>No activity yet.</div> : (
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead><tr style={{ color: C.muted, textAlign: "left" }}><th style={{ padding: "4px 6px" }}>Date</th><th style={{ padding: "4px 6px" }}>Type</th><th style={{ padding: "4px 6px" }}>Who</th><th style={{ padding: "4px 6px", textAlign: "right" }}>Amount</th></tr></thead>
            <tbody>
              {feed.map((r, i) => (
                <tr key={i} style={{ borderTop: "1px solid #eceae4" }}>
                  <td style={{ padding: "5px 6px" }}>{r.date}</td>
                  <td style={{ padding: "5px 6px" }}>{r.label}{r.source === "bankFeed" ? " 🏦" : ""}</td>
                  <td style={{ padding: "5px 6px" }}>{r.who}</td>
                  <td style={{ padding: "5px 6px", textAlign: "right", color: r.amount < 0 ? C.red : C.green, fontWeight: 600 }}>{money(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// A single pending join request the chair/approver acts on — pick a role, optionally
// grant permissions, then Approve (activates them) or Decline.
function PendingApprovalRow({ req, onApprove, onReject }) {
  const [role, setRole] = useState("member");
  const [perms, setPerms] = useState({});
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setPerms((p) => ({ ...p, [id]: !p[id] }));
  return (
    <div style={{ border: "1px solid #f0c98a", background: "#fffdf6", borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <div>
          <div style={{ fontWeight: 700, color: C.navy, fontSize: 13 }}>{req.name || req.email || "New member"}</div>
          <div style={{ fontSize: 11, color: C.muted }}>{req.email}</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select value={role} onChange={(e) => setRole(e.target.value)} style={{ fontSize: 12, padding: "5px 8px", border: "1px solid #e0ddd8", borderRadius: 6 }}>
            <option value="member">Member</option><option value="treasurer">Treasurer</option><option value="deputy">Deputy Chair</option><option value="secretary">Secretary</option>
          </select>
          <Btn kind="green" disabled={busy} onClick={async () => { setBusy(true); try { await onApprove(req.uid, role, perms); } finally { setBusy(false); } }}>{busy ? "Approving…" : "Approve"}</Btn>
          <Btn kind="danger" disabled={busy} onClick={() => onReject(req.uid, req.name || req.email)}>Decline</Btn>
        </div>
      </div>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 8, alignItems: "center" }}>
        {[["money", "Can record money"], ["approve", "Can approve members"]].map(([id, label]) => (
          <label key={id} style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 4, cursor: "pointer", color: C.navy }}>
            <input type="checkbox" checked={!!perms[id]} onChange={() => toggle(id)} />{label}
          </label>
        ))}
        <span style={{ fontSize: 10.5, color: C.muted }}>(A treasurer can always record money.)</span>
      </div>
    </div>
  );
}

// ── Member-scoped detail (full-screen overlay) — web parity with the native app ──
// Opened from Members / Contributions / Loans / Payouts. `focus` picks the sections;
// every section is filterable by date range. Read-only view over the group's records.
const MEMBER_RANGES = [["30d", "30 days"], ["3m", "3 months"], ["6m", "6 months"], ["year", "This year"], ["all", "All time"]];
function memberRangeStart(range) {
  const now = new Date();
  if (range === "all") return "";
  if (range === "year") return now.getFullYear() + "-01-01";
  const d = new Date(now);
  if (range === "30d") d.setDate(d.getDate() - 30);
  else if (range === "3m") d.setMonth(d.getMonth() - 3);
  else if (range === "6m") d.setMonth(d.getMonth() - 6);
  return d.toISOString().slice(0, 10);
}
function resolveMemberObj(m, stokvel) {
  const uid = m.uid || m.memberUid || null;
  const reference = m.reference || "";
  const name = m.name || m.memberName || "Member";
  const key = m.key || (uid || ("ref:" + normRef(reference)));
  const role = (uid && stokvel && stokvel.members && stokvel.members[uid] && stokvel.members[uid].role) || m.role || "member";
  return { key, uid, name, reference, role };
}
function contribMatchesMember(c, m) {
  return memberKey(c) === m.key || (m.uid && c.memberUid === m.uid) || (m.reference && normRef(c.reference) === normRef(m.reference));
}
function loanMatchesMember(l, m) {
  return (m.uid && l.memberUid === m.uid) || (m.reference && l.reference && normRef(l.reference) === normRef(m.reference)) || (m.name && l.memberName === m.name);
}
function payoutMatchesMember(p, m) {
  return (m.uid && p.memberUid === m.uid) || (m.name && p.memberName === m.name) || (m.reference && p.reference && normRef(p.reference) === normRef(m.reference));
}
function addPeriods(date, freq, n) { const x = new Date(date); if (freq === "annual") x.setFullYear(x.getFullYear() + n); else if (freq === "weekly") x.setDate(x.getDate() + 7 * n); else if (freq === "fortnightly") x.setDate(x.getDate() + 14 * n); else x.setMonth(x.getMonth() + n); return x; }
function periodAnchor(startISO, freq) { const d = new Date(startISO + "T00:00:00"); if (isNaN(d.getTime())) return new Date(); if (freq === "annual") return new Date(d.getFullYear(), 0, 1); if (freq === "weekly" || freq === "fortnightly") return d; return new Date(d.getFullYear(), d.getMonth(), 1); }
function periodLabel(d, freq) { const mon = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]; if (freq === "annual") return String(d.getFullYear()); if (freq === "weekly" || freq === "fortnightly") return d.getDate() + " " + mon[d.getMonth()] + " " + String(d.getFullYear()).slice(2); return mon[d.getMonth()] + " " + d.getFullYear(); }
function buildPaymentProfile(contribs, expected, freq, startISO) {
  const dated = (contribs || []).filter((c) => c.date);
  if (!dated.length) return { periods: [], missed: 0, short: 0, onTime: 0 };
  const firstISO = dated.map((c) => c.date).sort()[0];
  const from = (startISO && startISO > firstISO) ? startISO : firstISO;
  let cur = periodAnchor(from, freq);
  const now = new Date(); const periods = []; let guard = 0;
  while (cur <= now && guard < 500) {
    const next = addPeriods(cur, freq, 1);
    const s = cur.toISOString().slice(0, 10), e = next.toISOString().slice(0, 10);
    const paid = dated.filter((c) => c.date >= s && c.date < e).reduce((a, c) => a + (Number(c.amount) || 0), 0);
    let status = paid > 0 ? "paid" : "none";
    if (expected > 0) status = paid + 0.01 >= expected ? "paid" : paid > 0 ? "short" : "missed";
    periods.push({ key: s, label: periodLabel(cur, freq), paid, status });
    cur = next; guard += 1;
  }
  periods.reverse();
  return { periods, missed: periods.filter((p) => p.status === "missed").length, short: periods.filter((p) => p.status === "short").length, onTime: periods.filter((p) => p.status === "paid").length };
}

function MemberDetailView({ member, data, focus, onClose }) {
  const [range, setRange] = useState("all");
  const { stokvel } = data;
  const expected = Number(stokvel.contributionAmount) || 0;
  const freq = stokvel.frequency || "monthly";
  const start = memberRangeStart(range);
  const inRange = (dt) => !start || String(dt) >= start;

  const allContribs = (data.contributions || []).filter((c) => c.status !== "void" && contribMatchesMember(c, member));
  const rangeContribs = allContribs.filter((c) => inRange(c.date));
  const contribs = [...rangeContribs].sort((a, b) => String(b.date).localeCompare(String(a.date)));
  const loans = loanSummaries(data.loans, data.repayments).filter((l) => loanMatchesMember(l, member) && inRange(l.issueDate));
  const payouts = (data.payouts || []).filter((p) => payoutMatchesMember(p, member) && inRange(p.date)).sort((a, b) => String(b.date).localeCompare(String(a.date)));

  const contribTotal = rangeContribs.reduce((s, c) => s + (Number(c.amount) || 0), 0);
  const payoutTotal = payouts.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const borrowed = loans.reduce((s, l) => s + (Number(l.principal) || 0), 0);
  const loanRepaid = loans.reduce((s, l) => s + l.repaid, 0);
  const loanInterestTot = loans.reduce((s, l) => s + (Number(l.interest) || 0), 0);
  const loanOutstanding = loans.reduce((s, l) => s + l.outstanding, 0);

  const showC = focus === "all" || focus === "contributions";
  const showL = focus === "all" || focus === "loans";
  const showP = focus === "all" || focus === "payouts";
  const profile = showC ? buildPaymentProfile(rangeContribs, expected, freq, start) : null;
  const title = focus === "contributions" ? "Contributions" : focus === "loans" ? "Loans" : focus === "payouts" ? "Payouts" : "Member details";

  let kpis = [];
  if (focus === "contributions") kpis = [["Contributed", money(contribTotal), true], ["Payments", String(rangeContribs.length)], ["Missed", String(profile ? profile.missed : 0)]];
  else if (focus === "loans") kpis = [["Borrowed", money(borrowed), true], ["Interest", money(loanInterestTot)], ["Outstanding", money(loanOutstanding)]];
  else if (focus === "payouts") kpis = [["Received", money(payoutTotal), true], ["Payouts", String(payouts.length)]];
  else kpis = [["Contributed", money(contribTotal), true], ["Loan owing", money(loanOutstanding)], ["Received", money(payoutTotal)]];

  const tone = (s) => (s === "paid" ? "ok" : s === "short" ? "gold" : s === "missed" ? "bad" : "info");
  const intLabel = (l) => l.interestType === "none" ? "No interest" : l.interestType === "flat" ? (l.interestRate + "% flat (once-off)") : l.interestType === "compound" ? (l.interestRate + "% per month, compounding") : (l.interestRate + "% per month");
  const kv = (k, v) => (<div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderTop: "1px solid " + C.line }}><span style={{ fontSize: 12.5, color: C.muted }}>{k}</span><span style={{ fontSize: 12.5, color: C.navy, fontWeight: 600 }}>{v}</span></div>);
  const kpiBox = (label, value, accent) => (<div key={label} style={{ flex: 1, minWidth: 120, background: accent ? C.navy : "#fff", border: "1px solid " + (accent ? C.navy : C.line), borderRadius: 10, padding: 12 }}><div style={{ fontSize: 10, letterSpacing: 1, textTransform: "uppercase", fontWeight: 700, color: accent ? "#8b8ba0" : C.muted }}>{label}</div><div style={{ fontSize: 18, fontWeight: 800, color: accent ? C.gold : C.navy, marginTop: 3 }}>{value}</div></div>);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 3000, background: C.cream, overflowY: "auto" }}>
      <div style={{ position: "sticky", top: 0, background: C.navy, padding: "12px 18px", display: "flex", alignItems: "center", gap: 14, zIndex: 1 }}>
        <button onClick={onClose} style={{ background: "none", border: "1px solid #3a3a4f", color: "#e8e4dc", borderRadius: 6, padding: "5px 12px", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>‹ Back</button>
        <span style={{ fontSize: 14, fontWeight: 700, color: "#e8e4dc" }}>{title}</span>
      </div>
      <div style={{ maxWidth: 860, margin: "0 auto", padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: C.navy }}>{member.name}</div>
          <Badge tone={member.role === "chair" ? "gold" : member.role === "treasurer" ? "info" : "ok"}>{member.role}</Badge>
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>{member.reference ? "Ref " + member.reference : ""}</div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
          {MEMBER_RANGES.map(([id, label]) => (
            <button key={id} onClick={() => setRange(id)} style={{ fontSize: 12, fontWeight: 600, padding: "6px 12px", borderRadius: 16, cursor: "pointer", border: "1px solid " + (range === id ? C.navy : C.line), background: range === id ? C.navy : "#fff", color: range === id ? C.gold : C.navy }}>{label}</button>
          ))}
        </div>

        <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
          {kpis.map(([label, value, accent]) => kpiBox(label, value, accent))}
        </div>

        {showC && (
          <div style={card}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>Payment profile</div>
            <div style={{ fontSize: 11.5, color: C.muted, margin: "2px 0 8px" }}>{expected > 0 ? "Expected " + money(expected) + " / " + freqLabel(freq) : "No expected amount set — showing payments per " + freqLabel(freq) + "."}</div>
            {profile && profile.periods.length > 0 && (
              <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                <Badge tone="ok">{profile.onTime} on time</Badge>
                {profile.short > 0 && <Badge tone="gold">{profile.short} short</Badge>}
                {profile.missed > 0 && <Badge tone="bad">{profile.missed} missed</Badge>}
              </div>
            )}
            {!profile || profile.periods.length === 0 ? <div style={{ fontSize: 12.5, color: C.muted }}>No contributions in this period.</div> :
              profile.periods.map((p) => (
                <div key={p.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderTop: "1px solid " + C.line }}>
                  <span style={{ fontSize: 12.5, color: C.ink, flex: 1 }}>{p.label}</span>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: C.navy, width: 100, textAlign: "right", marginRight: 10 }}>{money(p.paid)}</span>
                  <Badge tone={tone(p.status)}>{p.status === "none" ? "—" : p.status}</Badge>
                </div>
              ))}
          </div>
        )}

        {showC && (
          <div style={card}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.navy, marginBottom: 4 }}>Contributions ({contribs.length})</div>
            {contribs.length === 0 ? <div style={{ fontSize: 12.5, color: C.muted }}>No contributions in this period.</div> :
              contribs.map((c) => (
                <div key={c.id} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderTop: "1px solid " + C.line }}>
                  <div><div style={{ fontSize: 12.5, color: C.ink, fontWeight: 600 }}>{c.date}</div><div style={{ fontSize: 11, color: C.muted }}>{(c.method || "bank") + (c.source === "bankFeed" ? " · 🏦 imported" : "") + (c.reference ? " · " + c.reference : "")}</div></div>
                  <div style={{ fontSize: 13, color: C.green, fontWeight: 700 }}>{money(c.amount)}</div>
                </div>
              ))}
          </div>
        )}

        {showL && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.navy, margin: "4px 0 4px" }}>Loans ({loans.length})</div>
            {loans.length > 0 && <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 8 }}>Borrowed {money(borrowed)} · interest charged {money(loanInterestTot)} · repaid {money(loanRepaid)} · outstanding {money(loanOutstanding)}</div>}
            {loans.length === 0 ? <div style={card}><span style={{ fontSize: 12.5, color: C.muted }}>No loans in this period.</span></div> :
              loans.map((l) => {
                const reps = (data.repayments || []).filter((r) => r.loanId === l.id).sort((a, b) => String(a.date).localeCompare(String(b.date)));
                const expectedTotal = (Number(l.principal) || 0) + (Number(l.interest) || 0);
                let bal = expectedTotal;
                return (
                  <div key={l.id} style={card}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: C.navy }}>Loan · {l.issueDate}</div>
                      <Badge tone={l.effectiveStatus === "settled" ? "ok" : l.effectiveStatus === "written_off" ? "bad" : "gold"}>{l.effectiveStatus === "written_off" ? "written off" : l.effectiveStatus}</Badge>
                    </div>
                    {kv("Principal", money(l.principal))}
                    {kv("Interest rate", intLabel(l))}
                    {kv("Interest accrued", money(l.interest))}
                    {kv("Repaid", money(l.repaid))}
                    {kv("Outstanding", money(l.outstanding))}
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, margin: "10px 0 2px" }}>Repayment history</div>
                    <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid " + C.line, fontSize: 11.5, color: C.muted }}><span>{l.issueDate} · loan issued</span><span style={{ fontWeight: 600 }}>{money(expectedTotal)}</span></div>
                    {reps.length === 0 ? <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>No repayments yet.</div> :
                      reps.map((r, i) => { bal = Math.max(0, bal - (Number(r.amount) || 0)); return (
                        <div key={r.id || i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderTop: "1px solid " + C.line }}>
                          <span style={{ fontSize: 12, color: C.ink, flex: 1 }}>{r.date}</span>
                          <span style={{ fontSize: 12, color: C.green, fontWeight: 600, width: 100, textAlign: "right" }}>{money(r.amount)}</span>
                          <span style={{ fontSize: 12, fontWeight: 600, width: 110, textAlign: "right" }}>{money(bal)}</span>
                        </div>
                      ); })}
                  </div>
                );
              })}
          </div>
        )}

        {showP && (
          <div style={card}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.navy, marginBottom: 4 }}>Payouts received ({payouts.length})</div>
            {payouts.length === 0 ? <div style={{ fontSize: 12.5, color: C.muted }}>No payouts in this period.</div> :
              payouts.map((p) => (
                <div key={p.id} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderTop: "1px solid " + C.line }}>
                  <div><div style={{ fontSize: 12.5, color: C.ink, fontWeight: 600 }}>{p.date}</div>{p.reason ? <div style={{ fontSize: 11, color: C.muted }}>{p.reason}</div> : null}</div>
                  <div style={{ fontSize: 13, color: C.red, fontWeight: 700 }}>{money(p.amount)}</div>
                </div>
              ))}
          </div>
        )}

        <Btn kind="primary" onClick={onClose} style={{ marginTop: 8 }}>‹ Back</Btn>
      </div>
    </div>
  );
}

function MembersTab({ data, onOpenMember, myRole, requests = [], canApprove, onAddMember, onInvite, onUpdateMember, onRemoveMember, onApprove, onReject }) {
  const { stokvel, contributions } = data;
  const summaries = memberSummaries(stokvel, contributions);
  const canManage = myRole === "chair";
  const copy = (txt) => { try { navigator.clipboard.writeText(txt); } catch (e) {} };
  const [editing, setEditing] = useState(null); // member key being edited
  const [editRef, setEditRef] = useState("");
  const [editOpen, setEditOpen] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const startEdit = (m) => { setEditing(m.key); setEditRef(m.reference || ""); setEditOpen(String(m.opening || 0)); setEditEmail(((stokvel.members || {})[m.key] || {}).email || ""); };
  const saveEdit = (m) => {
    const ref = editRef.trim();
    if (ref.replace(/[^A-Za-z0-9]/g, "").length < 3) return alert("Reference must be at least 3 letters/numbers so payments can auto-match.");
    const dup = Object.entries(stokvel.members || {}).some(([k, mm]) => k !== m.key && normRef(mm.reference) === normRef(ref));
    if (dup) return alert("Another member already uses that reference — give this member a distinct one.");
    const patch = { reference: ref, openingBalance: Number(editOpen) || 0 };
    // Only tracked members get an editable email here (it's the link key for a future
    // app invite); app members' email comes from their sign-in account.
    if (m.key.startsWith("tracked:")) patch.email = editEmail.trim().toLowerCase();
    onUpdateMember(m.key, patch);
    setEditing(null);
  };
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ fontSize: 13, color: C.muted }}>Everyone sees this. {summaries.length} member(s).</div>
        {canManage && <div style={{ display: "flex", gap: 8 }}>
          <Btn kind="gold" onClick={onInvite}>Invite with code</Btn>
          <Btn kind="primary" onClick={onAddMember}>+ Add member</Btn>
        </div>}
      </div>

      {canApprove && requests.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#8a6d1d", marginBottom: 6 }}>⏳ {requests.length} {requests.length === 1 ? "person is" : "people are"} waiting for approval</div>
          {requests.map((r) => <PendingApprovalRow key={r.uid} req={r} onApprove={onApprove} onReject={onReject} />)}
        </div>
      )}
      {canManage && <div style={{ background: "#eef6f0", border: "1px solid #cfe3d6", borderRadius: 8, padding: "8px 12px", fontSize: 11, color: "#2f5c40", marginBottom: 10 }}>Migrating an existing stokvel? Click <strong>Edit</strong> on a member to keep the <strong>reference they already use</strong> and set their <strong>opening balance</strong> (what they’ve contributed so far), so nothing resets to zero.</div>}
      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#f4f2ed", color: "#7a7568", textAlign: "left" }}>
            <th style={{ padding: "8px 10px" }}>Member</th><th style={{ padding: "8px 10px" }}>Role</th><th style={{ padding: "8px 10px" }}>Reference</th><th style={{ padding: "8px 10px", textAlign: "right" }}>Contributed</th><th style={{ padding: "8px 10px" }}>Last</th><th style={{ padding: "8px 10px" }}>Status</th>{canManage && <th style={{ padding: "8px 10px" }}></th>}
          </tr></thead>
          <tbody>
            {summaries.map((m) => {
              const isEditing = editing === m.key;
              return (
              <React.Fragment key={m.key}>
              <tr style={{ borderTop: "1px solid #eceae4" }}>
                <td style={{ padding: "8px 10px", fontWeight: 600 }}><span onClick={() => onOpenMember(m, "all")} title="View full member profile" style={{ color: C.navy, cursor: "pointer", borderBottom: "1px dotted " + C.gold }}>{m.name} ›</span>{m.email ? <div style={{ fontSize: 10, color: C.muted, fontWeight: 400 }}>{m.email}</div> : null}</td>
                <td style={{ padding: "8px 10px" }}><Badge tone={m.role === "chair" ? "gold" : m.role === "treasurer" ? "info" : "ok"}>{m.role}</Badge></td>
                <td style={{ padding: "8px 10px" }}>
                  {isEditing
                    ? <input value={editRef} onChange={(e) => setEditRef(e.target.value)} style={{ width: 90, fontFamily: "monospace", textTransform: "uppercase", fontSize: 11, padding: "3px 5px", border: "1px solid " + C.gold, borderRadius: 4 }} />
                    : <span title="Copy" onClick={() => copy(m.reference)} style={{ cursor: "pointer", fontFamily: "monospace", background: "#fff", border: "1px solid " + C.line, borderRadius: 4, padding: "2px 6px" }}>{m.reference} ⧉</span>}
                </td>
                <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600 }}>
                  {money(m.total)}
                  {isEditing
                    ? <div style={{ marginTop: 4 }}><input type="number" value={editOpen} onChange={(e) => setEditOpen(e.target.value)} title="Opening balance" style={{ width: 90, fontSize: 11, padding: "3px 5px", border: "1px solid " + C.gold, borderRadius: 4, textAlign: "right" }} /><div style={{ fontSize: 9, color: C.muted }}>opening bal.</div></div>
                    : (m.opening ? <div style={{ fontSize: 9, color: C.muted, fontWeight: 400 }}>incl. {money(m.opening)} b/f</div> : null)}
                </td>
                <td style={{ padding: "8px 10px" }}>{m.last ? m.last.date + " · " + money(m.last.amount) : "—"}</td>
                <td style={{ padding: "8px 10px" }}>{m.upToDate ? <Badge tone="ok">up to date</Badge> : <Badge tone="bad">behind</Badge>}</td>
                {canManage && <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                  {isEditing ? (
                    <>
                      <button onClick={() => saveEdit(m)} style={{ background: C.green, border: "none", color: "#fff", borderRadius: 4, padding: "3px 9px", fontSize: 11, cursor: "pointer" }}>Save</button>
                      <button onClick={() => setEditing(null)} style={{ marginLeft: 6, background: "none", border: "1px solid #e0ddd8", color: C.navy, borderRadius: 4, padding: "3px 9px", fontSize: 11, cursor: "pointer" }}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <select value={m.role} onChange={(e) => onUpdateMember(m.key, { role: e.target.value })} style={{ fontSize: 11, padding: 3, border: "1px solid #e0ddd8", borderRadius: 4 }}>
                        <option value="member">member</option><option value="treasurer">treasurer</option><option value="deputy">deputy chair</option><option value="secretary">secretary</option><option value="chair">chair</option>
                      </select>
                      <button onClick={() => startEdit(m)} style={{ marginLeft: 6, background: "none", border: "1px solid #d8d3c8", color: C.navy, borderRadius: 4, padding: "2px 7px", fontSize: 11, cursor: "pointer" }}>Edit</button>
                      <button onClick={() => onRemoveMember(m.key, m.name)} style={{ marginLeft: 6, background: "none", border: "1px solid #e8cccc", color: C.red, borderRadius: 4, padding: "2px 7px", fontSize: 11, cursor: "pointer" }}>Remove</button>
                    </>
                  )}
                </td>}
              </tr>
              </React.Fragment>
            );})}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Permissions & roles tab (chair only). Lists every member with their classification;
// the chair taps a member to open a screen to change their role and set permissions.
const STOKVEL_ROLE_OPTIONS = [["member", "Member"], ["treasurer", "Treasurer"], ["deputy", "Deputy Chair"], ["secretary", "Secretary"], ["chair", "Chairperson"]];
function roleTone(r) { return r === "chair" ? "gold" : r === "member" ? "ok" : "info"; }

function PermissionsTab({ data, myRole, onUpdateMember }) {
  const { stokvel } = data;
  const canManage = myRole === "chair";
  const [editKey, setEditKey] = useState(null);
  const summaries = memberSummaries(stokvel, data.contributions);
  const editing = editKey ? summaries.find((m) => m.key === editKey) : null;
  return (
    <div>
      <div style={{ background: "#eef0f6", border: "1px solid #d2dae8", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#3d567a", marginBottom: 14 }}>
        Every member and their classification. {canManage ? "Click a member to change their role and set what they can and cannot do." : "Only the chairperson can change roles and permissions."} The <strong>chair always has full access</strong>. Changes save immediately.
      </div>
      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#f4f2ed", color: "#7a7568", textAlign: "left" }}><th style={{ padding: "8px 10px" }}>Member</th><th style={{ padding: "8px 10px" }}>Classification</th>{canManage && <th style={{ padding: "8px 10px" }}></th>}</tr></thead>
          <tbody>
            {summaries.map((m) => (
              <tr key={m.key} onClick={canManage ? () => setEditKey(m.key) : undefined} style={{ borderTop: "1px solid #eceae4", cursor: canManage ? "pointer" : "default" }}>
                <td style={{ padding: "9px 10px", fontWeight: 600, color: C.navy }}>{m.name}{m.reference ? <span style={{ fontWeight: 400, color: C.muted, fontSize: 11 }}> · {m.reference}</span> : null}</td>
                <td style={{ padding: "9px 10px" }}><Badge tone={roleTone(m.role)}>{STOKVEL_ROLE_LABELS[m.role] || m.role}</Badge></td>
                {canManage && <td style={{ padding: "9px 10px", color: C.gold, fontWeight: 700, whiteSpace: "nowrap" }}>Manage ›</td>}
              </tr>
            ))}
            {summaries.length === 0 && <tr><td colSpan={canManage ? 3 : 2} style={{ padding: 16, textAlign: "center", color: C.muted }}>No members yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && <MemberPermissionsEditor member={editing} stokvel={stokvel} canManage={canManage} onUpdateMember={onUpdateMember} onClose={() => setEditKey(null)} />}
    </div>
  );
}

// Full-screen editor: change a member's classification (role) and allocate permissions.
function MemberPermissionsEditor({ member, stokvel, canManage, onUpdateMember, onClose }) {
  const cur = (stokvel.members || {})[member.key] || null;
  const inMap = !!cur;
  const role = (cur && cur.role) || member.role || "member";
  const perms = (cur && cur.perms) || { money: true };
  const isChair = role === "chair";
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 3000, background: C.cream, overflowY: "auto" }}>
      <div style={{ position: "sticky", top: 0, background: C.navy, padding: "12px 18px", display: "flex", alignItems: "center", gap: 14, zIndex: 1 }}>
        <button onClick={onClose} style={{ background: "none", border: "1px solid #3a3a4f", color: "#e8e4dc", borderRadius: 6, padding: "5px 12px", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>‹ Back</button>
        <span style={{ fontSize: 14, fontWeight: 700, color: "#e8e4dc" }}>Role & permissions</span>
      </div>
      <div style={{ maxWidth: 720, margin: "0 auto", padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: C.navy }}>{member.name}</div>
          <Badge tone={roleTone(role)}>{STOKVEL_ROLE_LABELS[role] || role}</Badge>
        </div>
        {!inMap ? (
          <div style={card}><div style={{ fontSize: 13, color: C.muted }}>This is a <strong>tracked contributor</strong> without an app login, so roles and permissions don't apply. Their contributions are still recorded and visible to everyone.</div></div>
        ) : (
          <>
            <div style={card}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>Classification</div>
              <div style={{ fontSize: 11.5, color: C.muted, margin: "2px 0 10px" }}>This member's role in the group.</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {STOKVEL_ROLE_OPTIONS.map(([id, label]) => (
                  <button key={id} disabled={!canManage} onClick={() => onUpdateMember(member.key, { role: id })} style={{ fontSize: 13, fontWeight: 600, padding: "8px 14px", borderRadius: 8, cursor: canManage ? "pointer" : "default", border: "1px solid " + (role === id ? C.navy : C.line), background: role === id ? C.navy : "#fff", color: role === id ? C.gold : C.navy }}>{label}</button>
                ))}
              </div>
            </div>
            <div style={card}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>Permissions</div>
              <div style={{ fontSize: 11.5, color: C.muted, margin: "2px 0 10px" }}>What this member can and cannot do.</div>
              {isChair ? (
                <div style={{ fontSize: 13, color: C.navy }}>👑 The chairperson has <strong>full access to everything</strong> — individual permissions don't apply.</div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
                  {PERMS.map((p) => (
                    <label key={p.id} style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8, cursor: canManage ? "pointer" : "default", color: C.navy }}>
                      <input type="checkbox" checked={!!perms[p.id]} disabled={!canManage} onChange={(e) => onUpdateMember(member.key, { perms: { ...perms, [p.id]: e.target.checked } })} />{p.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
        <Btn kind="primary" onClick={onClose} style={{ marginTop: 8 }}>‹ Done</Btn>
      </div>
    </div>
  );
}

// ── Corrections (dual sign-off) ──────────────────────────────────────────
// The fields a chair/treasurer may correct on each kind of money entry. The
// correction never edits the entry directly — it is proposed, then the OTHER
// office-bearer confirms it (see proposeAmendment/confirmAmendment).
const AMEND_FIELDS = {
  contributions: [
    { key: "amount", label: "Amount (R)", type: "number" },
    { key: "date", label: "Date", type: "date" },
    { key: "note", label: "Note", type: "text" },
  ],
  loans: [
    { key: "principal", label: "Loan amount / principal (R)", type: "number" },
    { key: "interestRate", label: "Interest rate (%)", type: "number" },
    { key: "issueDate", label: "Issue date", type: "date" },
    { key: "note", label: "Note", type: "text" },
  ],
  repayments: [
    { key: "amount", label: "Repayment amount (R)", type: "number" },
    { key: "date", label: "Date", type: "date" },
    { key: "note", label: "Note", type: "text" },
  ],
};
const KIND_LABEL = { contributions: "contribution", loans: "loan", repayments: "repayment" };
function amendValueLabel(key, v) {
  if (v == null || v === "") return "—";
  if (key === "amount" || key === "principal") return money(v);
  if (key === "interestRate") return v + "%";
  return String(v);
}

// Modal to PROPOSE a correction. Prefills the current values; only fields the user
// actually changes are submitted (with a mandatory reason for the audit trail).
function AmendModal({ target, onSubmit, onClose }) {
  const { kind, entry } = target;
  const fields = AMEND_FIELDS[kind] || [];
  const [vals, setVals] = useState(() => {
    const o = {};
    fields.forEach((f) => { o[f.key] = entry[f.key] != null ? String(entry[f.key]) : ""; });
    return o;
  });
  const [reason, setReason] = useState("");
  const u = (k, v) => setVals((s) => ({ ...s, [k]: v }));
  const submit = () => {
    const before = {}, changes = {};
    fields.forEach((f) => {
      const orig = entry[f.key] != null ? entry[f.key] : (f.type === "number" ? 0 : "");
      let nv = vals[f.key];
      if (f.type === "number") nv = Number(nv) || 0;
      const origCmp = f.type === "number" ? (Number(orig) || 0) : String(orig || "");
      const newCmp = f.type === "number" ? nv : String(nv || "");
      if (origCmp !== newCmp) { before[f.key] = orig; changes[f.key] = nv; }
    });
    if (!Object.keys(changes).length) return alert("Nothing has changed — edit a value to correct it.");
    if (!reason.trim()) return alert("Please give a reason for the correction — it is recorded in the audit trail.");
    onSubmit({ kind, targetId: entry.id, before, changes, reason: reason.trim(), memberName: entry.memberName || "" });
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(20,18,14,0.55)", zIndex: 1100, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 12, maxWidth: 460, width: "100%", padding: 20, marginTop: 40 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: C.navy }}>Propose a correction</div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>Correcting a {KIND_LABEL[kind] || "entry"}{entry.memberName ? " for " + entry.memberName : ""}. This does <strong>not</strong> change the record yet — the other office-bearer (chairperson ↔ treasurer) must confirm it first.</div>
        {fields.map((f) => (
          <div key={f.key}>
            <label style={lbl}>{f.label}</label>
            <input style={inp} type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={vals[f.key]} onChange={(e) => u(f.key, e.target.value)} />
          </div>
        ))}
        <label style={lbl}>Reason for the correction *</label>
        <textarea style={{ ...inp, minHeight: 64, resize: "vertical" }} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Amount captured as R500 but the deposit was R550" />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn kind="primary" onClick={submit}>Submit for confirmation</Btn>
        </div>
      </div>
    </div>
  );
}

// Panel listing pending corrections. Everyone sees them (transparency); only the
// COUNTERPART office-bearer (and not the proposer) can confirm; proposer or
// counterpart can reject.
function PendingAmendments({ amendments, myRole, myUid, canAct, onConfirm, onReject }) {
  const list = (amendments || []).filter((a) => a.status === "pending");
  if (!list.length) return null;
  return (
    <div style={{ background: "#fff8ec", border: "1px solid #f0c98a", borderRadius: 10, padding: 14, marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: "#8a6d1d", marginBottom: 8 }}>⚖️ Pending corrections — awaiting a second signature ({list.length})</div>
      {list.map((a) => {
        const changed = Object.keys(a.changes || {});
        const neededRole = a.proposedByRole === "treasurer" ? "chair" : "treasurer";
        const neededLabel = neededRole === "chair" ? "chairperson" : "treasurer";
        const isProposer = myUid && a.proposedBy === myUid;
        const canConfirm = canAct && (myRole === neededRole) && !isProposer;
        return (
          <div key={a.id} style={{ borderTop: "1px solid #f0dcb5", padding: "10px 0", fontSize: 12.5 }}>
            <div style={{ color: C.navy, fontWeight: 700 }}>Correction to a {KIND_LABEL[a.kind] || "entry"}{a.memberName ? " for " + a.memberName : ""}</div>
            {changed.map((k) => (
              <div key={k} style={{ color: C.ink, marginTop: 3 }}>
                {(AMEND_FIELDS[a.kind]?.find((f) => f.key === k)?.label) || k}: <span style={{ color: C.red, textDecoration: "line-through" }}>{amendValueLabel(k, a.before ? a.before[k] : undefined)}</span> → <span style={{ color: C.green, fontWeight: 700 }}>{amendValueLabel(k, a.changes[k])}</span>
              </div>
            ))}
            {a.reason ? <div style={{ color: C.muted, marginTop: 4 }}>Reason: {a.reason}</div> : null}
            <div style={{ fontSize: 10.5, color: "#b0863d", marginTop: 4 }}>Proposed by {a.proposedByName || "—"} ({a.proposedByRole}){a.at && a.at.toDate ? " · " + a.at.toDate().toLocaleString("en-ZA", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</div>
            <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
              {canConfirm ? (
                <>
                  <Btn kind="green" onClick={() => { if (window.confirm("Confirm this correction? It will be applied to the record.")) onConfirm(a.id); }}>Confirm & apply</Btn>
                  <button onClick={() => { const r = window.prompt("Reason for rejecting (optional):", ""); if (r !== null) onReject(a.id, r); }} style={{ background: "none", border: "1px solid #e8cccc", color: C.red, borderRadius: 6, padding: "6px 12px", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>Reject</button>
                </>
              ) : isProposer ? (
                <>
                  <span style={{ fontSize: 11.5, color: "#8a6d1d" }}>Waiting for the {neededLabel} to confirm.</span>
                  <button onClick={() => { if (window.confirm("Cancel this proposed correction?")) onReject(a.id, "cancelled by proposer"); }} style={{ background: "none", border: "1px solid " + C.line, color: C.muted, borderRadius: 6, padding: "6px 12px", fontSize: 12, cursor: "pointer" }}>Cancel</button>
                </>
              ) : (
                <span style={{ fontSize: 11.5, color: "#8a6d1d" }}>Needs the {neededLabel} to confirm.</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ContributionsTab({ data, onOpenMember, canRecord, onAmend, onAdd, onAddAll, onVoid }) {
  const { stokvel, contributions } = data;
  const members = Object.entries(stokvel.members || {});
  const [fm, setFm] = useState({ key: "__all__", date: todayIso(), amount: stokvel.contributionAmount || "", method: "cash", note: "" });
  const u = (k, v) => setFm((s) => ({ ...s, [k]: v }));
  const submit = () => {
    if (!(Number(fm.amount) > 0)) return alert("Enter an amount");
    const amount = Number(fm.amount);
    if (fm.key === "__all__") {
      if (!members.length) return alert("No members to record for yet");
      const list = members.map(([k, m]) => ({ memberUid: k.startsWith("tracked:") ? null : k, memberName: m.name, reference: m.reference, date: fm.date, amount, method: fm.method, note: fm.note }));
      if (!window.confirm("Record " + money(amount) + " for all " + members.length + " members?")) return;
      onAddAll(list);
    } else {
      const m = (stokvel.members || {})[fm.key];
      if (!m) return alert("Pick a member");
      onAdd({ memberUid: fm.key.startsWith("tracked:") ? null : fm.key, memberName: m.name, reference: m.reference, date: fm.date, amount, method: fm.method, note: fm.note });
    }
    setFm((s) => ({ ...s, amount: stokvel.contributionAmount || "", note: "" }));
  };
  const [rangeFrom, setRangeFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); });
  const [rangeTo, setRangeTo] = useState(todayIso());
  const shown = contributions.filter((c) => (!rangeFrom || c.date >= rangeFrom) && (!rangeTo || c.date <= rangeTo));
  const dateChip = { fontSize: 11, padding: "3px 6px", borderRadius: 5, border: "1px solid #3a3a4f", background: "#1a1a2e", color: "#e8e4dc" };
  return (
    <div>
      {canRecord && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 2 }}>Record a contribution</div>
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1fr auto", gap: 10, alignItems: "end" }}>
            <div><label style={lbl}>Member</label><select style={{ ...inp, cursor: "pointer" }} value={fm.key} onChange={(e) => u("key", e.target.value)}><option value="__all__">★ All members ({members.length})</option>{members.map(([k, m]) => <option key={k} value={k}>{m.name} ({m.reference})</option>)}</select></div>
            <div><label style={lbl}>Date</label><input style={inp} type="date" value={fm.date} onChange={(e) => u("date", e.target.value)} /></div>
            <div><label style={lbl}>Amount (R)</label><input style={inp} type="number" value={fm.amount} onChange={(e) => u("amount", e.target.value)} /></div>
            <div><label style={lbl}>Method</label><select style={{ ...inp, cursor: "pointer" }} value={fm.method} onChange={(e) => u("method", e.target.value)}><option value="cash">Cash</option><option value="bank">Bank/EFT</option><option value="manual">Other</option></select></div>
            <Btn kind="green" onClick={submit}>Record</Btn>
          </div>
        </div>
      )}
      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ background: C.navy, color: C.gold, fontSize: 12, padding: "8px 12px", fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span>Contributions ledger — {shown.filter((c) => c.status !== "void").length} of {contributions.filter((c) => c.status !== "void").length}</span>
          <span style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 400, color: "#cdc7bb" }}>
            From <input type="date" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} style={dateChip} />
            To <input type="date" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} style={dateChip} />
            <button onClick={() => { setRangeFrom(""); setRangeTo(""); }} style={{ ...dateChip, cursor: "pointer" }} title="Show all">All</button>
          </span>
        </div>
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
          <thead><tr style={{ color: C.muted, textAlign: "left" }}><th style={{ padding: "6px 10px" }}>Date</th><th style={{ padding: "6px 10px" }}>Member</th><th style={{ padding: "6px 10px" }}>Ref</th><th style={{ padding: "6px 10px" }}>Method</th><th style={{ padding: "6px 10px", textAlign: "right" }}>Amount</th>{canRecord && <th></th>}</tr></thead>
          <tbody>
            {shown.map((c) => (
              <tr key={c.id} style={{ borderTop: "1px solid #eceae4", opacity: c.status === "void" ? 0.4 : 1 }}>
                <td style={{ padding: "6px 10px" }}>{c.date}</td>
                <td style={{ padding: "6px 10px", fontWeight: 600 }}><span onClick={() => onOpenMember(c, "contributions")} title="View this member's contributions" style={{ color: C.navy, cursor: "pointer", borderBottom: "1px dotted " + C.gold }}>{c.memberName} ›</span></td>
                <td style={{ padding: "6px 10px", fontFamily: "monospace" }}>{c.reference}</td>
                <td style={{ padding: "6px 10px" }}>{c.method}{c.source === "bankFeed" ? " 🏦" : ""}</td>
                <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 600, color: C.green }}>{money(c.amount)}</td>
                {canRecord && <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>{c.status !== "void" && <>{onAmend && <button onClick={() => onAmend("contributions", c)} style={{ background: "none", border: "none", color: C.navy, cursor: "pointer", fontSize: 11, marginRight: 8 }}>correct</button>}<button onClick={() => onVoid(c.id)} style={{ background: "none", border: "none", color: C.red, cursor: "pointer", fontSize: 11 }}>void</button></>}</td>}
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={canRecord ? 6 : 5} style={{ padding: 16, textAlign: "center", color: C.muted }}>{contributions.length ? "No contributions in this date range." : "No contributions yet."}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LoansTab({ data, onOpenMember, canRecord, onAmend, onAddLoan, onAddRepayment, onUpdateLoan }) {
  const { stokvel, loans, repayments } = data;
  const members = Object.entries(stokvel.members || {});
  const sums = loanSummaries(loans, repayments);
  const [lf, setLf] = useState({ key: members[0] ? members[0][0] : "", principal: "", interestType: stokvel.loanInterestType || "monthly", interestRate: stokvel.loanRate != null ? String(stokvel.loanRate) : "", numberOfRepayments: "", issueDate: todayIso(), dueDate: "", note: "" });
  const [rf, setRf] = useState({ loanId: "", date: todayIso(), amount: "", note: "" });
  const [detail, setDetail] = useState(null);
  const ul = (k, v) => setLf((s) => ({ ...s, [k]: v }));
  const ur = (k, v) => setRf((s) => ({ ...s, [k]: v }));
  const activeLoans = sums.filter((l) => l.effectiveStatus === "active");
  const submitLoan = () => {
    const m = (stokvel.members || {})[lf.key];
    if (!m) return alert("Pick a member");
    if (!(Number(lf.principal) > 0)) return alert("Enter a principal amount");
    onAddLoan({ memberUid: lf.key.startsWith("tracked:") ? null : lf.key, memberName: m.name, reference: m.reference, principal: Number(lf.principal), interestType: lf.interestType, interestRate: Number(lf.interestRate) || 0, numberOfRepayments: Number(lf.numberOfRepayments) || 0, issueDate: lf.issueDate, dueDate: lf.dueDate || null, note: lf.note });
    setLf((s) => ({ ...s, principal: "", interestRate: "", numberOfRepayments: "", note: "" }));
  };
  const intShort = (l) => l.interestType === "none" ? "—" : l.interestType === "flat" ? (l.interestRate + "% flat") : l.interestType === "compound" ? (l.interestRate + "%/mo comp.") : (l.interestRate + "%/mo");
  const submitRepay = () => {
    const loan = sums.find((l) => l.id === rf.loanId);
    if (!loan) return alert("Pick a loan");
    if (!(Number(rf.amount) > 0)) return alert("Enter an amount");
    onAddRepayment({ loanId: loan.id, memberUid: loan.memberUid, memberName: loan.memberName, date: rf.date, amount: Number(rf.amount), note: rf.note });
    setRf((s) => ({ ...s, amount: "", note: "" }));
  };
  return (
    <div>
      <div style={{ background: "#eef0f6", border: "1px solid #d2dae8", borderRadius: 8, padding: "8px 12px", fontSize: 11, color: "#3d567a", marginBottom: 12 }}>
        Recording a loan or repayment does <strong>not</strong> move money — the group disburses and collects from its own account. This tracks who owes what (with interest) and keeps it visible to all members.
      </div>
      {canRecord && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 2 }}>Record a loan to a member</div>
          <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1.3fr 0.7fr 0.8fr 1fr auto", gap: 10, alignItems: "end" }}>
            <div><label style={lbl}>Member</label><select style={{ ...inp, cursor: "pointer" }} value={lf.key} onChange={(e) => ul("key", e.target.value)}>{members.map(([k, m]) => <option key={k} value={k}>{m.name}</option>)}</select></div>
            <div><label style={lbl}>Principal (R)</label><input style={inp} type="number" value={lf.principal} onChange={(e) => ul("principal", e.target.value)} /></div>
            <div><label style={lbl}>Interest</label><select style={{ ...inp, cursor: "pointer" }} value={lf.interestType} onChange={(e) => ul("interestType", e.target.value)}>{INTEREST_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}</select></div>
            <div><label style={lbl}>Rate %</label><input style={inp} type="number" value={lf.interestRate} onChange={(e) => ul("interestRate", e.target.value)} disabled={lf.interestType === "none"} /></div>
            <div><label style={lbl} title="How many repayments the member has agreed to make">Repayments</label><input style={inp} type="number" value={lf.numberOfRepayments} onChange={(e) => ul("numberOfRepayments", e.target.value)} placeholder="e.g. 3" /></div>
            <div><label style={lbl}>Issue date</label><input style={inp} type="date" value={lf.issueDate} onChange={(e) => ul("issueDate", e.target.value)} /></div>
            <Btn kind="green" onClick={submitLoan}>Add loan</Btn>
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>Interest: <strong>flat</strong> = once-off % · <strong>simple/mo</strong> = %×months on the principal · <strong>compound/mo</strong> = interest on interest. “Repayments” is the agreed number of instalments; outstanding always shows the live balance.</div>
        </div>
      )}

      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ background: C.navy, color: C.gold, fontSize: 12, padding: "8px 12px", fontWeight: 600 }}>Loans ({sums.length}) — outstanding {money(activeLoans.reduce((s, l) => s + l.outstanding, 0))}</div>
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
          <thead><tr style={{ color: C.muted, textAlign: "left" }}><th style={{ padding: "6px 10px" }}>Member</th><th style={{ padding: "6px 10px" }}>Issued</th><th style={{ padding: "6px 10px", textAlign: "right" }}>Principal</th><th style={{ padding: "6px 10px" }}>Interest</th><th style={{ padding: "6px 10px", textAlign: "right" }}>To settle</th><th style={{ padding: "6px 10px", textAlign: "right" }}>Repaid</th><th style={{ padding: "6px 10px" }}>Progress</th><th style={{ padding: "6px 10px", textAlign: "right" }}>Outstanding</th><th style={{ padding: "6px 10px" }}>Status</th></tr></thead>
          <tbody>
            {sums.map((l) => (
              <tr key={l.id} style={{ borderTop: "1px solid #eceae4" }}>
                <td style={{ padding: "6px 10px", fontWeight: 600 }}><span onClick={() => onOpenMember(l, "loans")} title="View this member's loans" style={{ color: C.navy, cursor: "pointer", borderBottom: "1px dotted " + C.gold }}>{l.memberName} ›</span></td>
                <td style={{ padding: "6px 10px" }}>{l.issueDate}</td>
                <td style={{ padding: "6px 10px", textAlign: "right" }}>{money(l.principal)}</td>
                <td style={{ padding: "6px 10px", fontSize: 11 }}>{intShort(l)}{l.interest > 0 ? <span style={{ color: C.muted }}> · +{money(l.interest)}</span> : null}</td>
                <td style={{ padding: "6px 10px", textAlign: "right" }}>{money(l.expectedTotal)}</td>
                <td style={{ padding: "6px 10px", textAlign: "right", color: C.green }}>{money(l.repaid)}</td>
                <td style={{ padding: "6px 10px", fontSize: 11, color: C.muted }}>{l.targetCount > 0 ? l.repaymentCount + " of " + l.targetCount : (l.repaymentCount + " paid")}</td>
                <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700, color: l.outstanding > 0 ? C.navy : C.green }}>{money(l.outstanding)}</td>
                <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>{l.effectiveStatus === "settled" ? <Badge tone="ok">settled</Badge> : l.effectiveStatus === "written_off" ? <Badge tone="bad">written off</Badge> : <Badge tone="warn">active</Badge>}<button onClick={() => setDetail(l)} style={{ marginLeft: 6, background: "none", border: "1px solid " + C.line, color: C.navy, borderRadius: 4, padding: "2px 7px", cursor: "pointer", fontSize: 10, fontWeight: 600 }}>Details</button>{canRecord && onAmend && <button onClick={() => onAmend("loans", (data.loans || []).find((x) => x.id === l.id) || l)} style={{ marginLeft: 6, background: "none", border: "none", color: C.navy, cursor: "pointer", fontSize: 10 }}>correct</button>}{canRecord && l.effectiveStatus === "active" && l.status !== "written_off" && <button onClick={() => { if (window.confirm("Write off this loan as unrecoverable?")) onUpdateLoan(l.id, { status: "written_off" }); }} style={{ marginLeft: 6, background: "none", border: "none", color: C.red, cursor: "pointer", fontSize: 10 }}>write off</button>}</td>
              </tr>
            ))}
            {sums.length === 0 && <tr><td colSpan={9} style={{ padding: 16, textAlign: "center", color: C.muted }}>No loans recorded.</td></tr>}
          </tbody>
        </table>
      </div>

      {canRecord && activeLoans.length > 0 && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 2 }}>Record a repayment</div>
          <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr auto", gap: 10, alignItems: "end" }}>
            <div><label style={lbl}>Loan</label><select style={{ ...inp, cursor: "pointer" }} value={rf.loanId} onChange={(e) => ur("loanId", e.target.value)}><option value="">Select a loan…</option>{activeLoans.map((l) => <option key={l.id} value={l.id}>{l.memberName} — {money(l.outstanding)} outstanding</option>)}</select></div>
            <div><label style={lbl}>Date</label><input style={inp} type="date" value={rf.date} onChange={(e) => ur("date", e.target.value)} /></div>
            <div><label style={lbl}>Amount (R)</label><input style={inp} type="number" value={rf.amount} onChange={(e) => ur("amount", e.target.value)} /></div>
            <Btn kind="green" onClick={submitRepay}>Record</Btn>
          </div>
        </div>
      )}
      {detail && <LoanDetailModal loan={detail} repayments={repayments} intShort={intShort} canRecord={canRecord} onAmend={onAmend} onClose={() => setDetail(null)} />}
    </div>
  );
}

// Full per-loan reconciliation — a FULL-SCREEN page (not a modal): terms + a
// running-balance list of every repayment.
function LoanDetailModal({ loan, repayments, intShort, canRecord, onAmend, onClose }) {
  const mine = (repayments || []).filter((r) => r.loanId === loan.id).sort((a, b) => String(a.date).localeCompare(String(b.date)));
  const intLabel = loan.interestType === "none" ? "No interest" : loan.interestType === "flat" ? (loan.interestRate + "% flat (once-off)") : loan.interestType === "compound" ? (loan.interestRate + "% per month, compounding") : (loan.interestRate + "% per month, simple");
  let bal = loan.expectedTotal;
  const rows = mine.map((r) => { bal = Math.max(0, bal - (Number(r.amount) || 0)); return { ...r, balAfter: bal }; });
  const KV = ({ k, v, bold }) => <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "6px 0", borderTop: "1px solid #eceae4" }}><span style={{ color: C.muted }}>{k}</span><span style={{ color: C.navy, fontWeight: bold ? 700 : 500 }}>{v}</span></div>;
  return (
    <div style={{ position: "fixed", inset: 0, background: "#f5f4f0", zIndex: 1000, overflowY: "auto", fontFamily: "'DM Sans','Segoe UI',sans-serif", color: "#2c2a26" }}>
      <div style={{ background: "#111118", color: "#e8e4dc", padding: "12px 20px", display: "flex", alignItems: "center", gap: 14 }}>
        <button onClick={onClose} style={{ background: "none", border: "1px solid #2a2a4a", color: "#e8e4dc", borderRadius: 7, padding: "7px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>← Back to loans</button>
        <div style={{ fontSize: 15, fontWeight: 700 }}>Loan reconciliation</div>
      </div>
      <div style={{ maxWidth: 820, margin: "0 auto", padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: C.navy }}>{loan.memberName}</div>
            <div style={{ fontSize: 12, color: C.muted }}>Loan issued {loan.issueDate}</div>
          </div>
          <Badge tone={loan.effectiveStatus === "settled" ? "ok" : loan.effectiveStatus === "written_off" ? "bad" : "warn"}>{loan.effectiveStatus === "written_off" ? "written off" : loan.effectiveStatus}</Badge>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
          <div style={{ flex: "1 1 200px", background: C.navy, borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 10, letterSpacing: 1, color: "#8b8ba0", textTransform: "uppercase", fontWeight: 700 }}>Total to settle</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: C.gold, marginTop: 4 }}>{money(loan.expectedTotal)}</div>
          </div>
          <div style={{ flex: "1 1 200px", background: "#fff", border: "1px solid " + C.line, borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 10, letterSpacing: 1, color: C.muted, textTransform: "uppercase", fontWeight: 700 }}>Repaid</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: C.green, marginTop: 4 }}>{money(loan.repaid)}</div>
          </div>
          <div style={{ flex: "1 1 200px", background: "#fff", border: "1px solid " + C.line, borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 10, letterSpacing: 1, color: C.muted, textTransform: "uppercase", fontWeight: 700 }}>Outstanding</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: loan.outstanding > 0 ? C.navy : C.green, marginTop: 4 }}>{money(loan.outstanding)}</div>
          </div>
        </div>

        <div style={{ ...card }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 2 }}>Loan terms</div>
          <KV k="Issued" v={loan.issueDate} />
          <KV k="Principal" v={money(loan.principal)} />
          <KV k="Interest terms" v={intLabel} />
          <KV k="Interest accrued to date" v={money(loan.interest)} />
          <KV k="Total to settle" v={money(loan.expectedTotal)} bold />
          <KV k="Agreed repayments" v={loan.targetCount > 0 ? loan.targetCount : "—"} />
        </div>

        <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, margin: "6px 0 6px" }}>Repayment history{loan.targetCount > 0 ? " — " + rows.length + " of " + loan.targetCount : ""}</div>
        <div style={{ ...card, padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead><tr style={{ background: "#f4f2ed", color: "#7a7568", textAlign: "left" }}><th style={{ padding: "8px 12px" }}>Date</th><th style={{ padding: "8px 12px", textAlign: "right" }}>Paid</th><th style={{ padding: "8px 12px", textAlign: "right" }}>Balance after</th>{canRecord && onAmend && <th></th>}</tr></thead>
            <tbody>
              <tr><td style={{ padding: "8px 12px", color: C.muted }}>{loan.issueDate} · loan issued</td><td style={{ padding: "8px 12px", textAlign: "right", color: C.muted }}>—</td><td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 600 }}>{money(loan.expectedTotal)}</td>{canRecord && onAmend && <td></td>}</tr>
              {rows.map((r, i) => (
                <tr key={r.id || i} style={{ borderTop: "1px solid #eceae4" }}>
                  <td style={{ padding: "8px 12px" }}>{r.date}{r.note ? <div style={{ fontSize: 10, color: C.muted }}>{r.note}</div> : null}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right", color: C.green, fontWeight: 600 }}>{money(r.amount)}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 600 }}>{money(r.balAfter)}</td>
                  {canRecord && onAmend && <td style={{ padding: "8px 12px", textAlign: "right" }}>{r.id && <button onClick={() => onAmend("repayments", { ...r, memberName: loan.memberName })} style={{ background: "none", border: "none", color: C.navy, cursor: "pointer", fontSize: 11 }}>correct</button>}</td>}
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={canRecord && onAmend ? 4 : 3} style={{ padding: 14, textAlign: "center", color: C.muted }}>No repayments recorded yet.</td></tr>}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 18 }}><Btn kind="primary" onClick={onClose}>← Back to loans</Btn></div>
      </div>
    </div>
  );
}

function PayoutsTab({ data, onOpenMember, canRecord, authUser, run, onAdd, onAddAll, onDelete }) {
  const { stokvel, payouts } = data;
  const members = Object.entries(stokvel.members || {});
  const meId = authUser && (authUser.email || authUser.uid);
  const [fm, setFm] = useState({ key: "", date: todayIso(), amount: "", reason: "" });
  const u = (k, v) => setFm((s) => ({ ...s, [k]: v }));
  const submit = () => {
    if (!(Number(fm.amount) > 0)) return alert("Enter an amount");
    const amount = Number(fm.amount);
    if (fm.key === "__all__") {
      if (!members.length) return alert("No members to pay yet");
      if (!window.confirm("Record a payout of " + money(amount) + " to each of the " + members.length + " members?")) return;
      const list = members.map(([k, m]) => ({ memberUid: k.startsWith("tracked:") ? null : k, memberName: m.name, date: fm.date, amount, reason: fm.reason }));
      onAddAll(list);
    } else {
      const m = fm.key ? (stokvel.members || {})[fm.key] : null;
      onAdd({ memberUid: fm.key && !fm.key.startsWith("tracked:") ? fm.key : null, memberName: m ? m.name : "Group", date: fm.date, amount, reason: fm.reason });
    }
    setFm((s) => ({ ...s, amount: "", reason: "" }));
  };
  return (
    <div>
      <div style={{ background: "#eef0f6", border: "1px solid #d2dae8", borderRadius: 8, padding: "8px 12px", fontSize: 11, color: "#3d567a", marginBottom: 12 }}>Recording a payout logs a disbursement the group made from its own account — the app does not transfer any money.</div>
      {canRecord && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 2 }}>Record a payout</div>
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1.4fr auto", gap: 10, alignItems: "end" }}>
            <div><label style={lbl}>To</label><select style={{ ...inp, cursor: "pointer" }} value={fm.key} onChange={(e) => u("key", e.target.value)}><option value="">Group / other</option><option value="__all__">★ All members ({members.length})</option>{members.map(([k, m]) => <option key={k} value={k}>{m.name}</option>)}</select></div>
            <div><label style={lbl}>Date</label><input style={inp} type="date" value={fm.date} onChange={(e) => u("date", e.target.value)} /></div>
            <div><label style={lbl}>Amount (R)</label><input style={inp} type="number" value={fm.amount} onChange={(e) => u("amount", e.target.value)} /></div>
            <div><label style={lbl}>Reason</label><input style={inp} value={fm.reason} onChange={(e) => u("reason", e.target.value)} placeholder="Year-end payout, etc." /></div>
            <Btn kind="green" onClick={submit}>Record</Btn>
          </div>
        </div>
      )}
      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ background: C.navy, color: C.gold, fontSize: 12, padding: "8px 12px", fontWeight: 600 }}>Payouts ({payouts.length})</div>
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
          <thead><tr style={{ color: C.muted, textAlign: "left" }}><th style={{ padding: "6px 10px" }}>Date</th><th style={{ padding: "6px 10px" }}>To</th><th style={{ padding: "6px 10px" }}>Reason</th><th style={{ padding: "6px 10px" }}>Proof / check</th><th style={{ padding: "6px 10px", textAlign: "right" }}>Amount</th>{canRecord && <th></th>}</tr></thead>
          <tbody>
            {payouts.map((p) => (
              <tr key={p.id} style={{ borderTop: "1px solid #eceae4" }}>
                <td style={{ padding: "6px 10px" }}>{p.date}</td>
                <td style={{ padding: "6px 10px", fontWeight: 600 }}>{p.memberName && p.memberName !== "Group" ? <span onClick={() => onOpenMember(p, "payouts")} title="View this member's payouts" style={{ color: C.navy, cursor: "pointer", borderBottom: "1px dotted " + C.gold }}>{p.memberName} ›</span> : (p.memberName || "Group")}</td>
                <td style={{ padding: "6px 10px" }}>{p.reason}</td>
                <td style={{ padding: "6px 10px" }}><MoneyOutVerify txn={p} kind="payouts" stokvelId={stokvel.id} canRecord={canRecord} meId={meId} run={run} /></td>
                <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 600, color: C.red }}>{money(-p.amount)}</td>
                {canRecord && <td style={{ padding: "6px 10px" }}><button onClick={() => { if (window.confirm("Delete this payout?")) onDelete(p.id); }} style={{ background: "none", border: "none", color: C.red, cursor: "pointer", fontSize: 11 }}>delete</button></td>}
              </tr>
            ))}
            {payouts.length === 0 && <tr><td colSpan={6} style={{ padding: 16, textAlign: "center", color: C.muted }}>No payouts yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// A member-visible receipt cell: image thumbnail or a PDF link, opening the full
// file in a new tab. Shown to EVERYONE so the whole group can verify a purchase.
function ReceiptCell({ receipt }) {
  if (!receipt || !receipt.url) return <span style={{ color: C.muted, fontSize: 11 }}>—</span>;
  if (receipt.kind === "image") {
    return <a href={receipt.url} target="_blank" rel="noopener noreferrer" title="View receipt"><img src={receipt.thumbUrl || receipt.url} alt="receipt" style={{ width: 34, height: 34, objectFit: "cover", borderRadius: 4, border: "1px solid " + C.line, cursor: "pointer" }} /></a>;
  }
  return <a href={receipt.url} target="_blank" rel="noopener noreferrer" style={{ color: C.navy, fontSize: 11, fontWeight: 600, textDecoration: "none", border: "1px solid " + C.line, borderRadius: 4, padding: "2px 6px" }}>📄 PDF</a>;
}

function sameActorId(a, b) { return !!a && !!b && String(a).toLowerCase() === String(b).toLowerCase(); }
const zar = (n) => "R" + (Number(n) || 0).toLocaleString("en-ZA");

// Upload one or more receipts, each with the amount shown on the slip, and attach them
// to a money-out entry. Attaching re-opens the second-person check.
function AttachReceiptsModal({ stokvelId, kind, docId, txnAmount, onClose, run }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const pick = (files) => setRows((r) => [...r, ...Array.from(files || []).map((f) => ({ file: f, amount: "" }))]);
  const setAmt = (i, v) => setRows((r) => r.map((x, k) => (k === i ? { ...x, amount: v } : x)));
  const rm = (i) => setRows((r) => r.filter((_, k) => k !== i));
  const total = rows.reduce((s, x) => s + (Number(x.amount) || 0), 0);
  const submit = async () => {
    if (!rows.length) return alert("Add at least one receipt file.");
    if (rows.some((x) => !(Number(x.amount) > 0))) return alert("Enter the amount shown on each receipt.");
    setBusy("Uploading…");
    try {
      const metas = await uploadReceipts(stokvelId, rows.map((x) => x.file));
      const withAmt = metas.map((m, i) => ({ ...m, amount: Number(rows[i].amount) || 0 }));
      await run(() => attachReceipts(stokvelId, kind, docId, withAmt));
      onClose();
    } catch (e) { alert(e.message || "Upload failed."); setBusy(""); }
  };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 10, padding: "18px 20px", maxWidth: 460, width: "100%" }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.navy }}>Attach receipt(s)</div>
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 3 }}>Upload a photo or PDF of each slip and enter its amount. A second person then checks the receipts match the money out{txnAmount ? " (" + zar(txnAmount) + ")" : ""}.</div>
        <input type="file" accept=".pdf,image/*" multiple style={{ marginTop: 12, fontSize: 12 }} onChange={(e) => pick(e.target.files)} />
        {rows.map((x, i) => (
          <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
            <span style={{ fontSize: 11, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>📎 {x.file.name}</span>
            <span style={{ fontSize: 11 }}>R</span>
            <input style={{ ...inp, width: 100, marginTop: 0 }} type="number" placeholder="amount" value={x.amount} onChange={(e) => setAmt(i, e.target.value)} />
            <button onClick={() => rm(i)} style={{ background: "none", border: "none", color: C.red, cursor: "pointer" }}>✕</button>
          </div>
        ))}
        {rows.length > 0 && <div style={{ fontSize: 11.5, marginTop: 8 }}>Receipts total: <strong>{zar(total)}</strong>{txnAmount ? " of " + zar(txnAmount) : ""}{txnAmount && total + 0.005 < Number(txnAmount) ? <span style={{ color: "#b5651d" }}> — short, will stay flagged</span> : null}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
          <button onClick={onClose} style={{ background: "none", border: "1px solid " + C.line, borderRadius: 6, padding: "7px 12px", cursor: "pointer" }}>Cancel</button>
          <Btn kind="green" onClick={submit} disabled={!!busy}>{busy || "Upload"}</Btn>
        </div>
      </div>
    </div>
  );
}

// Money-out verification cell: shows the verified/flagged badge (with the reconciliation
// reason), the attached receipts (amount + who/when on hover), and — for a DIFFERENT
// member than the uploader — a "Check & verify" action. Visible to all members.
function MoneyOutVerify({ txn, kind, stokvelId, canRecord, meId, run }) {
  const [attach, setAttach] = useState(false);
  const v = verificationState(txn);
  const recs = receiptsOfTxn(txn);
  const iAmUploader = sameActorId(txn.recordedBy, meId) || recs.some((r) => sameActorId(r.uploadedBy, meId));
  const canCheck = canRecord && v.reconciled && !v.confirmed && !iAmUploader;
  const when = (ms) => (ms ? new Date(ms).toLocaleDateString("en-ZA", { day: "numeric", month: "short" }) : "");
  const vAt = txn.verifiedAt && txn.verifiedAt.toMillis ? txn.verifiedAt.toMillis() : null;
  return (
    <div style={{ minWidth: 148 }}>
      {v.status === "verified"
        ? <span style={{ display: "inline-block", background: "#e7f3ec", color: C.green, borderRadius: 20, padding: "2px 8px", fontSize: 10.5, fontWeight: 700 }}>✓ Verified</span>
        : <span style={{ display: "inline-block", background: "#fff3d9", color: "#8a6d1d", borderRadius: 20, padding: "2px 8px", fontSize: 10.5, fontWeight: 700 }}>⚠ {v.reason}</span>}
      {recs.length > 0 && (
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 5 }}>
          {recs.map((r, i) => (
            <a key={i} href={r.url} target="_blank" rel="noopener noreferrer" title={zar(r.amount) + " · uploaded by " + (r.uploadedBy || "?") + " " + when(r.uploadedAt)} style={{ fontSize: 10, textDecoration: "none", border: "1px solid " + C.line, borderRadius: 4, padding: "1px 5px", color: C.navy }}>{r.kind === "image" ? "🖼" : "📄"} {zar(r.amount)}</a>
          ))}
        </div>
      )}
      {v.status === "verified" && txn.verifiedBy && <div style={{ fontSize: 9.5, color: C.muted, marginTop: 3 }}>by {txn.verifiedBy}{vAt ? " · " + when(vAt) : ""}</div>}
      {canRecord && v.status !== "verified" && (
        <div style={{ marginTop: 5, display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button onClick={() => setAttach(true)} style={{ fontSize: 10.5, background: "none", border: "1px solid " + C.line, borderRadius: 4, padding: "2px 7px", cursor: "pointer", color: C.navy }}>+ Receipt</button>
          {canCheck && <button onClick={() => run(() => setVerification(stokvelId, kind, txn.id, { verified: true }))} style={{ fontSize: 10.5, background: C.green, color: "#fff", border: "none", borderRadius: 4, padding: "2px 8px", cursor: "pointer", fontWeight: 700 }}>Check &amp; verify</button>}
          {v.reconciled && !v.confirmed && iAmUploader && <span style={{ fontSize: 9.5, color: C.muted, alignSelf: "center" }}>needs another member to verify</span>}
        </div>
      )}
      {attach && <AttachReceiptsModal stokvelId={stokvelId} kind={kind} docId={txn.id} txnAmount={txn.amount} onClose={() => setAttach(false)} run={run} />}
    </div>
  );
}

// Money out that isn't a payout/loan (bank fees, outings) + money in that isn't a
// contribution (bank interest earned). Both feed the group's cash position. Receipts
// are core (included in the single R250/month plan): each entry can carry an
// invoice/receipt that every member sees, for a verifiable audit trail.
function ExpensesTab({ data, canRecord, authUser, run, onAddExpense, onDeleteExpense, onAddIncome, onDeleteIncome }) {
  const { stokvel, expenses = [], income = [] } = data;
  const receiptsOn = subscriptionState(stokvel).hasReceipts; // receipts are core (kept for the income doc column)
  const stokvelId = stokvel.id;
  const meId = authUser && (authUser.email || authUser.uid);
  const [ef, setEf] = useState({ category: "bank_charge", date: todayIso(), amount: "", note: "" });
  const [inf, setInf] = useState({ category: "interest_earned", date: todayIso(), amount: "", note: "" });
  const [eFile, setEFile] = useState(null);
  const [iFile, setIFile] = useState(null);
  const [busyMsg, setBusyMsg] = useState("");
  const ue = (k, v) => setEf((s) => ({ ...s, [k]: v }));
  const ui = (k, v) => setInf((s) => ({ ...s, [k]: v }));

  const submitE = async () => {
    if (!(Number(ef.amount) > 0)) return alert("Enter an amount");
    // Record immediately (starts Unverified); receipts are attached per-row below.
    onAddExpense({ category: ef.category, date: ef.date, amount: Number(ef.amount), note: ef.note });
    setEf((s) => ({ ...s, amount: "", note: "" }));
  };
  const submitI = async () => {
    if (!(Number(inf.amount) > 0)) return alert("Enter an amount");
    let receipt = null;
    if (receiptsOn && iFile) {
      try { setBusyMsg("Uploading receipt…"); receipt = await uploadReceipt(stokvelId, iFile); }
      catch (err) { setBusyMsg(""); return alert(err.message || "Receipt upload failed"); }
      setBusyMsg("");
    }
    onAddIncome({ category: inf.category, date: inf.date, amount: Number(inf.amount), note: inf.note, receipt });
    setInf((s) => ({ ...s, amount: "", note: "" })); setIFile(null);
  };
  const delRow = (row, kind) => {
    if (!window.confirm("Delete this " + kind + "?")) return;
    if (row.receipt) removeReceipt(row.receipt).catch(() => {});
    (kind === "expense" ? onDeleteExpense : onDeleteIncome)(row.id);
  };

  const totalE = expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const totalI = income.reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const fileInputStyle = { fontSize: 11, marginTop: 4 };

  return (
    <div>
      <div style={{ background: "#eef0f6", border: "1px solid #d2dae8", borderRadius: 8, padding: "8px 12px", fontSize: 11, color: "#3d567a", marginBottom: 12 }}>Bank charges, interest earned and event costs move the group’s real balance. Record them here, or let the statement import pick them up automatically. Nothing here transfers money — it mirrors the bank account.</div>

      <div style={{ background: "#eef6f0", border: "1px solid #cfe3d6", borderRadius: 8, padding: "8px 12px", fontSize: 11.5, color: "#2f5c40", marginBottom: 12 }}>🧾 <strong>Every expense needs proof.</strong> Attach the receipt(s) with their amounts; the entry stays <strong>⚠ Unverified</strong> until the receipts add up to the full amount <strong>and</strong> a second member (not the uploader) checks them. Every member sees the status.</div>

      {/* Expenses */}
      {canRecord && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 2 }}>Record an expense / fee</div>
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1.4fr auto", gap: 10, alignItems: "end" }}>
            <div><label style={lbl}>Category</label><select style={{ ...inp, cursor: "pointer" }} value={ef.category} onChange={(e) => ue("category", e.target.value)}>{EXPENSE_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}</select></div>
            <div><label style={lbl}>Date</label><input style={inp} type="date" value={ef.date} onChange={(e) => ue("date", e.target.value)} /></div>
            <div><label style={lbl}>Amount (R)</label><input style={inp} type="number" value={ef.amount} onChange={(e) => ue("amount", e.target.value)} /></div>
            <div><label style={lbl}>Note</label><input style={inp} value={ef.note} onChange={(e) => ue("note", e.target.value)} placeholder="Monthly fee, year-end braai…" /></div>
            <Btn kind="green" onClick={submitE} disabled={!!busyMsg}>{busyMsg ? "…" : "Record"}</Btn>
          </div>
          <div style={{ marginTop: 8, fontSize: 10.5, color: C.muted }}>After recording, attach the receipt(s) in the table below — a second member then verifies them.</div>
          {busyMsg && <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>{busyMsg}</div>}
        </div>
      )}
      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ background: C.navy, color: C.gold, fontSize: 12, padding: "8px 12px", fontWeight: 600, display: "flex", justifyContent: "space-between" }}><span>Expenses &amp; fees ({expenses.length})</span><span>−{money(totalE)}</span></div>
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
          <thead><tr style={{ color: C.muted, textAlign: "left" }}><th style={{ padding: "6px 10px" }}>Date</th><th style={{ padding: "6px 10px" }}>Category</th><th style={{ padding: "6px 10px" }}>Note</th><th style={{ padding: "6px 10px" }}>Proof / check</th><th style={{ padding: "6px 10px", textAlign: "right" }}>Amount</th>{canRecord && <th></th>}</tr></thead>
          <tbody>
            {expenses.map((e) => (
              <tr key={e.id} style={{ borderTop: "1px solid #eceae4" }}>
                <td style={{ padding: "6px 10px" }}>{e.date}</td>
                <td style={{ padding: "6px 10px" }}>{expenseCategoryLabel(e.category)}{e.source === "statementImport" ? " 🏦" : ""}</td>
                <td style={{ padding: "6px 10px", color: C.muted }}>{e.note}</td>
                <td style={{ padding: "6px 10px" }}><MoneyOutVerify txn={e} kind="expenses" stokvelId={stokvelId} canRecord={canRecord} meId={meId} run={run} /></td>
                <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 600, color: C.red }}>{money(-e.amount)}</td>
                {canRecord && <td style={{ padding: "6px 10px" }}><button onClick={() => delRow(e, "expense")} style={{ background: "none", border: "none", color: C.red, cursor: "pointer", fontSize: 11 }}>delete</button></td>}
              </tr>
            ))}
            {expenses.length === 0 && <tr><td colSpan={6} style={{ padding: 16, textAlign: "center", color: C.muted }}>No expenses recorded.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Income */}
      {canRecord && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 2 }}>Record interest / income</div>
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1.4fr auto", gap: 10, alignItems: "end" }}>
            <div><label style={lbl}>Category</label><select style={{ ...inp, cursor: "pointer" }} value={inf.category} onChange={(e) => ui("category", e.target.value)}>{INCOME_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}</select></div>
            <div><label style={lbl}>Date</label><input style={inp} type="date" value={inf.date} onChange={(e) => ui("date", e.target.value)} /></div>
            <div><label style={lbl}>Amount (R)</label><input style={inp} type="number" value={inf.amount} onChange={(e) => ui("amount", e.target.value)} /></div>
            <div><label style={lbl}>Note</label><input style={inp} value={inf.note} onChange={(e) => ui("note", e.target.value)} placeholder="Credit interest…" /></div>
            <Btn kind="green" onClick={submitI} disabled={!!busyMsg}>{busyMsg ? "…" : "Record"}</Btn>
          </div>
          {receiptsOn && <div style={{ marginTop: 8 }}><label style={lbl}>Attach document (optional)</label><input type="file" accept=".pdf,image/*" style={fileInputStyle} onChange={(e) => setIFile(e.target.files && e.target.files[0])} />{iFile && <span style={{ fontSize: 11, color: C.green, marginLeft: 8 }}>✓ {iFile.name}</span>}</div>}
        </div>
      )}
      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ background: C.green, color: "#fff", fontSize: 12, padding: "8px 12px", fontWeight: 600, display: "flex", justifyContent: "space-between" }}><span>Interest &amp; income ({income.length})</span><span>+{money(totalI)}</span></div>
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
          <thead><tr style={{ color: C.muted, textAlign: "left" }}><th style={{ padding: "6px 10px" }}>Date</th><th style={{ padding: "6px 10px" }}>Category</th><th style={{ padding: "6px 10px" }}>Note</th>{receiptsOn && <th style={{ padding: "6px 10px" }}>Doc</th>}<th style={{ padding: "6px 10px", textAlign: "right" }}>Amount</th>{canRecord && <th></th>}</tr></thead>
          <tbody>
            {income.map((i) => (
              <tr key={i.id} style={{ borderTop: "1px solid #eceae4" }}>
                <td style={{ padding: "6px 10px" }}>{i.date}</td>
                <td style={{ padding: "6px 10px" }}>{incomeCategoryLabel(i.category)}{i.source === "statementImport" ? " 🏦" : ""}</td>
                <td style={{ padding: "6px 10px", color: C.muted }}>{i.note}</td>
                {receiptsOn && <td style={{ padding: "6px 10px" }}><ReceiptCell receipt={i.receipt} /></td>}
                <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 600, color: C.green }}>{money(i.amount)}</td>
                {canRecord && <td style={{ padding: "6px 10px" }}><button onClick={() => delRow(i, "income")} style={{ background: "none", border: "none", color: C.red, cursor: "pointer", fontSize: 11 }}>delete</button></td>}
              </tr>
            ))}
            {income.length === 0 && <tr><td colSpan={receiptsOn ? 6 : 5} style={{ padding: 16, textAlign: "center", color: C.muted }}>No income recorded.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SettingsTab({ data, myRole, authUser, canManageSettings, activeId, run }) {
  const { stokvel, pendingChanges = [] } = data;
  const [fm, setFm] = useState({ name: stokvel.name, type: stokvel.type, frequency: stokvel.frequency, contributionAmount: stokvel.contributionAmount, bankName: stokvel.bankName || "", accountName: stokvel.accountName || "", accountNumber: stokvel.accountNumber || "", branchCode: stokvel.branchCode || "", referenceScheme: stokvel.referenceScheme || "codeNumber", loanRate: stokvel.loanRate != null ? stokvel.loanRate : "", loanInterestType: stokvel.loanInterestType || "monthly", loanRepaymentFreq: stokvel.loanRepaymentFreq || "monthly", loanRepaymentDay: stokvel.loanRepaymentDay != null ? stokvel.loanRepaymentDay : "", openingCash: stokvel.openingCash != null ? stokvel.openingCash : "", openingDate: stokvel.openingDate || "", claimWaitingDays: stokvel.claimWaitingDays != null ? stokvel.claimWaitingDays : DEFAULT_CLAIM_WAITING_DAYS, coveredPerMemberLimit: stokvel.coveredPerMemberLimit != null ? stokvel.coveredPerMemberLimit : DEFAULT_COVERED_PER_MEMBER, joiningFee: stokvel.joiningFee != null ? stokvel.joiningFee : "", latePenalty: stokvel.latePenalty != null ? stokvel.latePenalty : "", graceDays: stokvel.graceDays != null ? stokvel.graceDays : "", absencePenalty: stokvel.absencePenalty != null ? stokvel.absencePenalty : "", coverAmounts: stokvel.coverAmounts || {} });
  const u = (k, v) => setFm((s) => ({ ...s, [k]: v }));
  const uCover = (rel, v) => setFm((s) => ({ ...s, coverAmounts: { ...s.coverAmounts, [rel]: v } }));
  const numOrNull = (v) => (v === "" || v == null ? null : Number(v) || 0);
  const canManage = !!canManageSettings;
  const isChair = myRole === "chair";
  const seconding = !!stokvel.requireSecondApproval;
  const myUid = authUser ? authUser.uid : "";
  const myName = (authUser && authUser.email) || "a manager";
  const [cbusy, setCbusy] = useState("");
  const [savedMsg, setSavedMsg] = useState("");
  const [activity, setActivity] = useState([]);
  React.useEffect(() => { loadStokvelAuditLog(stokvel.id, 60).then(setActivity).catch(() => {}); }, [stokvel.id, pendingChanges.length]);

  // Apply a settings change — directly, or as a proposal when seconding is on.
  const applyChange = (patch) => {
    if (seconding) { run(() => proposeStokvelChange(activeId, patch, myName)); setSavedMsg("Proposed — awaiting a second manager's approval."); }
    else { run(() => saveStokvel(activeId, patch)); setSavedMsg("Saved."); }
    setTimeout(() => setSavedMsg(""), 4000);
  };
  const saveAll = () => applyChange({ ...fm, contributionAmount: Number(fm.contributionAmount) || 0, bankLabel: bankLabelFrom(fm), openingCash: fm.openingCash === "" ? null : Number(fm.openingCash) || 0, openingDate: fm.openingDate || null, claimWaitingDays: Number(fm.claimWaitingDays) || 0, coveredPerMemberLimit: Number(fm.coveredPerMemberLimit) || 0, joiningFee: numOrNull(fm.joiningFee), latePenalty: numOrNull(fm.latePenalty), graceDays: numOrNull(fm.graceDays), absencePenalty: numOrNull(fm.absencePenalty), loanRate: numOrNull(fm.loanRate), loanRepaymentDay: numOrNull(fm.loanRepaymentDay), coverAmounts: Object.fromEntries(Object.entries(fm.coverAmounts || {}).filter(([, v]) => v !== "" && v != null).map(([k, v]) => [k, Number(v) || 0])) });
  const uploadConstitution = async (file) => {
    if (!file) return;
    setCbusy("Uploading…");
    try { const doc = await uploadReceipt(stokvel.id, file); applyChange({ constitution: doc }); setCbusy(""); }
    catch (e) { setCbusy(e.message || "Upload failed."); }
  };
  const [logoBusy, setLogoBusy] = useState("");
  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoBusy("Uploading…");
    try { const doc = await uploadReceipt(stokvel.id, file); applyChange({ logo: doc }); setLogoBusy(""); }
    catch (e) { setLogoBusy(e.message || "Upload failed."); }
  };
  const patchSummary = (patch) => Object.keys(patch || {}).filter((k) => k !== "updatedAt").join(", ") || "settings";
  return (
    <div>
    {pendingChanges.map((pc) => {
      const mine = pc.proposedBy === myUid;
      return (
        <div key={pc.id} style={{ background: "#fff7e6", border: "1px solid #f0d97f", borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#8a6d1d" }}>⏳ Rule change awaiting a second approval</div>
          <div style={{ fontSize: 12, color: "#7a5b00", marginTop: 4 }}>Proposed by <strong>{pc.proposedByName || "a manager"}</strong> — changes: <strong>{patchSummary(pc.patch)}</strong>.</div>
          {canManage && (mine
            ? <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>You proposed this — another manager must approve it. You can withdraw it.</div>
            : <div style={{ fontSize: 11, color: "#7a5b00", marginTop: 8 }}>As a second manager, you can approve or reject it.</div>)}
          {canManage && <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            {!mine && <Btn kind="green" onClick={() => run(() => approvePendingChange(activeId, pc.id))}>Approve &amp; apply</Btn>}
            <Btn kind="ghost" onClick={() => { if (window.confirm(mine ? "Withdraw this proposal?" : "Reject this proposal?")) run(() => rejectPendingChange(activeId, pc.id)); }} style={{ color: C.red }}>{mine ? "Withdraw" : "Reject"}</Btn>
          </div>}
        </div>
      );
    })}
    {savedMsg && <div style={{ background: "#eef6f0", border: "1px solid #cfe3d6", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12, color: "#2f5c40" }}>{savedMsg}</div>}
    <div style={card}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 4 }}>Group settings</div>
      {!canManage && <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>You don't have permission to change settings. The chairperson (or a treasurer granted “Change settings & rules”) can edit these.</div>}
      {canManage && seconding && <div style={{ fontSize: 11, color: "#8a6d1d", background: "#fff7e6", border: "1px solid #f0d97f", borderRadius: 6, padding: "6px 10px", marginBottom: 8 }}>Seconding is on: your changes are <strong>proposed</strong> and take effect only after another manager approves them.</div>}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div><label style={lbl}>Name</label><input style={inp} value={fm.name} disabled={!canManage} onChange={(e) => u("name", e.target.value)} /></div>
        <div><label style={lbl}>Reference code</label><input style={{ ...inp, background: "#f4f2ed" }} value={stokvel.code} disabled /></div>
        <div><label style={lbl}>Type</label><select style={{ ...inp, cursor: "pointer" }} value={fm.type} disabled={!canManage} onChange={(e) => u("type", e.target.value)}>{STOKVEL_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}</select></div>
        <div><label style={lbl}>Frequency</label><select style={{ ...inp, cursor: "pointer" }} value={fm.frequency} disabled={!canManage} onChange={(e) => u("frequency", e.target.value)}>{FREQUENCIES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}</select></div>
        <div><label style={lbl}>Expected contribution (R)</label><input style={inp} type="number" value={fm.contributionAmount} disabled={!canManage} onChange={(e) => u("contributionAmount", e.target.value)} /></div>
        <div><label style={lbl}>Member reference format</label><select style={{ ...inp, cursor: "pointer" }} value={fm.referenceScheme} disabled={!canManage} onChange={(e) => u("referenceScheme", e.target.value)}>{REFERENCE_SCHEMES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}</select><div style={{ fontSize: 10.5, color: C.muted, marginTop: 3 }}>How new members' payment references are auto-generated.</div></div>
      </div>

      <div style={{ fontSize: 12, fontWeight: 700, color: C.navy, marginTop: 16 }}>Group bank account <span style={{ fontWeight: 400, color: C.muted }}>— the group's own account (reference only; no money is held here)</span></div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div><label style={lbl}>Bank</label><input style={inp} value={fm.bankName} disabled={!canManage} onChange={(e) => u("bankName", e.target.value)} placeholder="e.g. Standard Bank" /></div>
        <div><label style={lbl}>Name on account</label><input style={inp} value={fm.accountName} disabled={!canManage} onChange={(e) => u("accountName", e.target.value)} placeholder="Account holder" /></div>
        <div><label style={lbl}>Account number</label><input style={inp} value={fm.accountNumber} disabled={!canManage} onChange={(e) => u("accountNumber", e.target.value)} placeholder="e.g. 10 1234 5678" /></div>
        <div><label style={lbl}>Branch code</label><input style={inp} value={fm.branchCode} disabled={!canManage} onChange={(e) => u("branchCode", e.target.value)} placeholder="e.g. 051001" /></div>
      </div>

      <div style={{ fontSize: 12, fontWeight: 700, color: C.navy, marginTop: 16 }}>Group logo <span style={{ fontWeight: 400, color: C.muted }}>— shown at the top of your stokvel (optional)</span></div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 6 }}>
        {stokvel.logo && stokvel.logo.url
          ? <img src={stokvel.logo.thumbUrl || stokvel.logo.url} alt="logo" style={{ width: 46, height: 46, objectFit: "cover", borderRadius: 8, border: "1px solid " + C.line }} />
          : <div style={{ width: 46, height: 46, borderRadius: 8, border: "1px dashed " + C.line, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, fontSize: 18 }}>🤝</div>}
        {canManage && <div><input type="file" accept="image/*" onChange={(e) => uploadLogo(e.target.files && e.target.files[0])} style={{ fontSize: 11 }} />{logoBusy && <span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>{logoBusy}</span>}</div>}
      </div>

      {canManage && <div style={{ marginTop: 16 }}><Btn kind="primary" onClick={saveAll}>{seconding ? "Propose settings change" : "Save settings"}</Btn></div>}
    </div>

    <div style={card}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 4 }}>Rules &amp; fees</div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.6 }}>Set from this stokvel's constitution. These are recorded and shown to members; the app flags them where it can. Leave blank for any your society doesn't use.</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div><label style={lbl}>Joining fee (R)</label><input style={inp} type="number" value={fm.joiningFee} disabled={!canManage} onChange={(e) => u("joiningFee", e.target.value)} placeholder="e.g. 100" /></div>
        <div><label style={lbl}>Grace period for a late payment (days)</label><input style={inp} type="number" value={fm.graceDays} disabled={!canManage} onChange={(e) => u("graceDays", e.target.value)} placeholder="e.g. 7" /></div>
        <div><label style={lbl}>Late-payment penalty (R)</label><input style={inp} type="number" value={fm.latePenalty} disabled={!canManage} onChange={(e) => u("latePenalty", e.target.value)} placeholder="e.g. 50" /></div>
        <div><label style={lbl}>Absence / meeting penalty (R)</label><input style={inp} type="number" value={fm.absencePenalty} disabled={!canManage} onChange={(e) => u("absencePenalty", e.target.value)} placeholder="e.g. 50" /></div>
      </div>
      <div style={{ fontSize: 10.5, color: C.muted, marginTop: 8 }}>Press <strong>Save settings</strong> above to apply. (Penalties are recorded here now; automatic charging can be switched on later.)</div>
    </div>

    <div style={card}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 4 }}>Loan rules</div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.6 }}>The society's default terms for loans to members. These <strong>pre-fill the loan form</strong> so the treasurer doesn't re-type them each time (they can still adjust per loan).</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div><label style={lbl}>Interest charged on loans (%)</label><input style={inp} type="number" value={fm.loanRate} disabled={!canManage} onChange={(e) => u("loanRate", e.target.value)} placeholder="e.g. 10" /></div>
        <div><label style={lbl}>Interest type</label><select style={{ ...inp, cursor: "pointer" }} value={fm.loanInterestType} disabled={!canManage} onChange={(e) => u("loanInterestType", e.target.value)}>{INTEREST_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}</select></div>
        <div><label style={lbl}>Repayment frequency</label><select style={{ ...inp, cursor: "pointer" }} value={fm.loanRepaymentFreq} disabled={!canManage} onChange={(e) => u("loanRepaymentFreq", e.target.value)}><option value="weekly">Weekly</option><option value="fortnightly">Fortnightly</option><option value="monthly">Monthly</option></select></div>
        <div><label style={lbl}>Repayment day of the month</label><input style={inp} type="number" min="1" max="31" value={fm.loanRepaymentDay} disabled={!canManage} onChange={(e) => u("loanRepaymentDay", e.target.value)} placeholder="e.g. 25 (for monthly)" /></div>
      </div>
      <div style={{ fontSize: 10.5, color: C.muted, marginTop: 8 }}>Interest type: <strong>simple per month</strong> = %×months on the principal · <strong>compound per month</strong> = interest on interest · <strong>flat</strong> = once-off %. Press <strong>Save settings</strong> above to apply.</div>
    </div>

    {fm.type === "burial" && (
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 4 }}>Cover amounts by relationship</div>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.6 }}>What the society pays out per type of covered person. When a claim is logged, the cover amount is filled in from here automatically (the chair can still adjust it).</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {BURIAL_RELATIONSHIPS.map((r) => (
            <div key={r.id}><label style={lbl}>{r.label} (R)</label><input style={inp} type="number" value={fm.coverAmounts[r.id] != null ? fm.coverAmounts[r.id] : ""} disabled={!canManage} onChange={(e) => uCover(r.id, e.target.value)} placeholder="0" /></div>
          ))}
        </div>
        <div style={{ fontSize: 10.5, color: C.muted, marginTop: 8 }}>Press <strong>Save settings</strong> above to apply.</div>
      </div>
    )}

    {fm.type === "burial" && (
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 4 }}>Burial-society rules</div>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.6 }}>These are set per stokvel, from your society's constitution. The app applies them on the <strong>Claims</strong> tab.</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div><label style={lbl}>Waiting period (days)</label><input style={inp} type="number" value={fm.claimWaitingDays} disabled={!canManage} onChange={(e) => u("claimWaitingDays", e.target.value)} placeholder="e.g. 180" /><div style={{ fontSize: 10.5, color: C.muted, marginTop: 3 }}>New covered people show in orange until this many days after they're added.</div></div>
          <div><label style={lbl}>People covered per member</label><input style={inp} type="number" value={fm.coveredPerMemberLimit} disabled={!canManage} onChange={(e) => u("coveredPerMemberLimit", e.target.value)} placeholder="e.g. 5" /><div style={{ fontSize: 10.5, color: C.muted, marginTop: 3 }}>Maximum family members each member may register.</div></div>
        </div>
        {canManage && <div style={{ fontSize: 10.5, color: C.muted, marginTop: 8 }}>Remember to press <strong>Save settings</strong> above after changing these.</div>}
      </div>
    )}

    <div style={card}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 4 }}>Constitution</div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.6 }}>Upload this stokvel's constitution (its rulebook) so every member can read the agreed rules. Visible to all members.</div>
      {stokvel.constitution && stokvel.constitution.url
        ? <a href={stokvel.constitution.url} target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", fontSize: 12, fontWeight: 600, color: C.navy, textDecoration: "none", border: "1px solid " + C.line, borderRadius: 6, padding: "6px 12px" }}>📄 {stokvel.constitution.name || "View constitution"}</a>
        : <div style={{ fontSize: 12, color: C.muted }}>No constitution uploaded yet.</div>}
      {canManage && <div style={{ marginTop: 10 }}>
        <label style={lbl}>{stokvel.constitution ? "Replace" : "Upload"} constitution (PDF or photo)</label>
        <input type="file" accept=".pdf,image/*" onChange={(e) => uploadConstitution(e.target.files && e.target.files[0])} style={{ fontSize: 11, marginTop: 4 }} />
        {cbusy && <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>{cbusy}</div>}
      </div>}
    </div>

    <div style={card}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 4 }}>Migration — opening balance</div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.6 }}>
        If this stokvel existed <strong>before</strong> the app, set the group’s cash pot as at a starting date so the Overview reflects reality instead of R0. This is the money already in the group’s account on that date (after all past payouts). Each member’s own history is set per-member on the <strong>Members</strong> tab (Edit → opening balance).
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div><label style={lbl}>Opening cash in the group account (R)</label><input style={inp} type="number" value={fm.openingCash} disabled={!canManage} onChange={(e) => u("openingCash", e.target.value)} placeholder="e.g. 48200" /></div>
        <div><label style={lbl}>As at date</label><input style={inp} type="date" value={fm.openingDate} disabled={!canManage} onChange={(e) => u("openingDate", e.target.value)} /></div>
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>After setting this, only record contributions/payouts dated <strong>after</strong> the opening date, so nothing is double-counted.</div>
    </div>

    <div style={card}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 4 }}>Governance — two-person approval</div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.6 }}>When on, any settings/rule change must be <strong>proposed by one manager and approved by another</strong> before it takes effect. Every proposal, approval and rejection is recorded in the log below.</div>
      <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8, color: C.navy, cursor: isChair ? "pointer" : "default" }}>
        <input type="checkbox" checked={seconding} disabled={!isChair} onChange={(e) => run(() => saveStokvel(activeId, { requireSecondApproval: e.target.checked }))} />
        Require a second manager to approve rule changes
      </label>
      {!isChair && <div style={{ fontSize: 10.5, color: C.muted, marginTop: 6 }}>Only the chairperson can turn this on or off.</div>}
    </div>

    <div style={card}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 4 }}>Activity &amp; audit log</div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>Every change to this group — who did what, and when. Append-only; it cannot be edited or deleted.</div>
      {activity.length === 0 ? <div style={{ fontSize: 12, color: C.muted }}>No activity recorded yet.</div> :
        <div style={{ maxHeight: 320, overflowY: "auto" }}>
          {activity.map((a) => (
            <div key={a.id} style={{ borderTop: "1px solid #eceae4", padding: "6px 0", fontSize: 12 }}>
              <div style={{ color: C.navy }}>{a.summary || a.action}</div>
              <div style={{ fontSize: 10.5, color: C.muted }}>{a.actor || "—"}{a.at && a.at.toDate ? " · " + a.at.toDate().toLocaleString("en-ZA", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</div>
            </div>
          ))}
        </div>}
    </div>
    </div>
  );
}

// Subscription status + subscribe controls. Shown above the tabs. The chair can
// start the single R250/month plan (everything included) via Paystack; everyone sees
// the current status. While inactive, recording is blocked (viewing stays open).
function SubscriptionBanner({ stokvel, myRole }) {
  const sub = subscriptionState(stokvel);
  const isChair = myRole === "chair";
  const [busy, setBusy] = useState("");
  const [showTerms, setShowTerms] = useState(false);
  const go = async (plan) => {
    setBusy(plan);
    try { await startSubscription(stokvel.id, plan); }
    catch (e) { setBusy(""); alert(e.message || "Could not start payment."); }
  };
  const onCancel = async () => {
    if (!window.confirm(`Cancel this subscription?\n\nBilling stops now — no further monthly charges. Your group keeps full access until ${sub.untilLabel}, then recording locks until you subscribe again. Viewing your records stays free.`)) return;
    setBusy("cancel");
    try {
      await cancelSubscription(stokvel.id);
      alert(`Subscription cancelled. No further charges. Your group stays active until ${sub.untilLabel}.`);
      window.location.reload();
    } catch (e) { alert(e.message || "Could not cancel the subscription."); }
    setBusy("");
  };

  // Recurring-billing disclosure shown wherever we ask the chair to subscribe.
  const recurringNote = (color) => (
    <div style={{ fontSize: 10.5, color, marginTop: 8, lineHeight: 1.5 }}>
      This is a <strong>recurring monthly subscription</strong> — your card is billed automatically each month until you cancel. Cancel any time from this panel (billing stops; access runs to the end of the paid month).{" "}
      <span onClick={() => setShowTerms(true)} style={{ textDecoration: "underline", cursor: "pointer" }}>Subscription terms</span>
    </div>
  );

  let body;
  if (sub.status === "paid" && sub.cancelled) {
    body = (
      <div style={{ background: "#fff8e6", border: "1px solid #f0d97f", borderRadius: 8, padding: "9px 13px", marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: "#7a5b00" }}>
          ⚠ <strong>Subscription cancelled</strong> — no further billing. Your group stays active until <strong>{sub.untilLabel}</strong>, then recording locks until you subscribe again.
        </div>
        {isChair && <div style={{ marginTop: 8 }}><Btn kind="green" disabled={!!busy} onClick={() => go("core_receipts")}>{busy ? "Redirecting…" : "Re-subscribe — R250/month"}</Btn></div>}
      </div>
    );
  } else if (sub.status === "paid") {
    body = (
      <div style={{ background: "#eef6f0", border: "1px solid #cfe3d6", borderRadius: 8, padding: "9px 13px", marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 12, color: "#2f5c40" }}>
          ✓ <strong>Subscription active</strong> — R250/mo (incl. receipts), renews monthly. Paid through <strong>{sub.untilLabel}</strong>.
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {isChair && <span onClick={busy ? undefined : onCancel} style={{ fontSize: 11, color: "#a5674f", textDecoration: "underline", cursor: busy ? "default" : "pointer" }}>{busy === "cancel" ? "Cancelling…" : "Cancel subscription"}</span>}
        </div>
      </div>
    );
  } else if (sub.status === "trial") {
    body = (
      <div style={{ background: "#eaf2fb", border: "1px solid #b9d4f0", borderRadius: 8, padding: "11px 14px", marginBottom: 12 }}>
        <div style={{ fontSize: 12.5, color: "#1b4a7a", fontWeight: 600, marginBottom: 3 }}>
          🎉 Free trial — <strong>{sub.trialDaysLeft} {sub.trialDaysLeft === 1 ? "day" : "days"} left</strong> (ends {sub.trialEndLabel})
        </div>
        <div style={{ fontSize: 11.5, color: "#1b4a7a", marginBottom: isChair ? 9 : 0 }}>
          You have full access — record contributions, loans, payouts, expenses and receipts. {isChair ? "Subscribe any time to keep going after the trial." : "Ask your chairperson to subscribe before it ends."}
        </div>
        {isChair && (
          <>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Btn kind="green" disabled={!!busy} onClick={() => go("core_receipts")}>{busy ? "Redirecting…" : "Subscribe — R250/month"}</Btn>
              <span style={{ fontSize: 10.5, color: "#5a7ea6", alignSelf: "center" }}>Secure payment via Paystack · card or EFT</span>
            </div>
            {recurringNote("#5a7ea6")}
          </>
        )}
      </div>
    );
  } else {
    body = (
      <div style={{ background: "#fff8e6", border: "1px solid #f0d97f", borderRadius: 8, padding: "11px 14px", marginBottom: 12 }}>
        <div style={{ fontSize: 12.5, color: "#7a5b00", fontWeight: 600, marginBottom: 3 }}>⏳ Your free trial has ended</div>
        <div style={{ fontSize: 11.5, color: "#7a5b00", marginBottom: isChair ? 9 : 0 }}>
          Members can still view the full books, but recording contributions, loans, payouts and expenses now needs an active subscription. {isChair ? "" : "Ask your chairperson to subscribe."}
        </div>
        {isChair && (
          <>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Btn kind="green" disabled={!!busy} onClick={() => go("core_receipts")}>{busy ? "Redirecting…" : "Subscribe — R250/month"}</Btn>
              <span style={{ fontSize: 10.5, color: "#9a8140", alignSelf: "center" }}>Secure payment via Paystack · card or EFT</span>
            </div>
            {recurringNote("#9a8140")}
          </>
        )}
      </div>
    );
  }
  return (<>{body}{showTerms && <SubscriptionTermsModal onClose={() => setShowTerms(false)} />}</>);
}

// The subscription's continuous-billing + cancellation terms — surfaced at the point
// of subscribing (POPIA/CPA fair-notice) and reusable as the T&Cs section.
function SubscriptionTermsModal({ onClose }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 10, padding: "20px 22px", maxWidth: 520, width: "100%", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.3)" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.navy, marginBottom: 10 }}>Subscription terms</div>
        <div style={{ fontSize: 12.5, color: "#333", lineHeight: 1.6 }}>
          <p><strong>Continuous monthly subscription.</strong> The Tlhakiso Stokvel subscription is a <strong>recurring monthly subscription</strong> of <strong>R250 per month</strong>, including receipts &amp; the audit trail. It begins after your 14-day free trial or when you subscribe.</p>
          <p><strong>Automatic billing.</strong> By subscribing you authorise Paystack to <strong>automatically debit your card each month</strong> for the applicable amount, until you cancel. You enter your card details once; no re-entry is needed each month.</p>
          <p><strong>How to cancel.</strong> The chairperson can cancel at any time using the <strong>“Cancel subscription”</strong> link in the subscription panel. Billing stops immediately (no further charges). Your group keeps <strong>full access until the end of the month you already paid for</strong>, after which recording is locked until you subscribe again. Viewing your records remains free at all times.</p>
          <p><strong>No partial refunds.</strong> Cancellation stops future billing; the current paid month is not refunded.</p>
          <p>Questions or cancellation help: <strong>support@tlhakiso.co.za</strong>.</p>
        </div>
        <div style={{ marginTop: 14, textAlign: "right" }}><Btn kind="green" onClick={onClose}>Close</Btn></div>
      </div>
    </div>
  );
}

// ───────────────────────────── Burial claims + covered family ─────────────────────────────

// A small view/link for an attached document (death certificate).
function DocLink({ doc: d }) {
  if (!d || !d.url) return <span style={{ color: C.muted, fontSize: 11 }}>—</span>;
  if (d.kind === "image") return <a href={d.url} target="_blank" rel="noopener noreferrer" title="View certificate"><img src={d.thumbUrl || d.url} alt="certificate" style={{ width: 30, height: 30, objectFit: "cover", borderRadius: 4, border: "1px solid " + C.line, cursor: "pointer", verticalAlign: "middle" }} /></a>;
  return <a href={d.url} target="_blank" rel="noopener noreferrer" style={{ color: C.navy, fontSize: 11, fontWeight: 600, textDecoration: "none", border: "1px solid " + C.line, borderRadius: 4, padding: "2px 6px" }}>📄 View</a>;
}

// Orange while in the waiting period, normal (green "Covered") once it's complete.
function CoverBadge({ dep, waitingDays }) {
  const s = coverStatus(dep.coveredFrom, waitingDays, todayIso());
  if (s.active) return <Badge tone="ok">Covered</Badge>;
  return <span style={waitingChip}>In waiting · until {s.waitingUntil}{s.daysLeft ? " (" + s.daysLeft + "d)" : ""}</span>;
}
const waitingChip = { background: "#ffe8cc", color: "#b5651d", border: "1px solid #f0c98a", borderRadius: 20, padding: "2px 9px", fontSize: 10, fontWeight: 700, display: "inline-block" };
function coveredLimitOf(stokvel) { return stokvel.coveredPerMemberLimit != null ? Number(stokvel.coveredPerMemberLimit) : DEFAULT_COVERED_PER_MEMBER; }
function waitingDaysOf(stokvel) { return stokvel.claimWaitingDays != null ? Number(stokvel.claimWaitingDays) : DEFAULT_CLAIM_WAITING_DAYS; }

function CoveredFamilyModal({ stokvel, dependants, canRecord, activeId, run, onClose }) {
  const memberEntries = Object.entries(stokvel.members || {});
  const [memberKey, setMemberKey] = useState(memberEntries[0] ? memberEntries[0][0] : "");
  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("spouse");
  const [dob, setDob] = useState("");
  const waitingDays = waitingDaysOf(stokvel);
  const limit = coveredLimitOf(stokvel);
  const member = stokvel.members[memberKey] || {};
  const mine = (dependants || []).filter((d) => d.memberKey === memberKey);
  const atLimit = limit > 0 && mine.length >= limit;
  const add = () => {
    if (!name.trim()) return alert("Enter the family member's name.");
    if (atLimit) return alert("This member already covers " + limit + " people — the society's limit. Remove one, or raise the limit in Settings.");
    run(() => addDependant(activeId, { memberKey, memberName: member.name || member.email || "Member", name: name.trim(), relationship, dob: dob.trim(), coveredFrom: todayIso() }));
    setName(""); setDob("");
  };
  return (
    <Modal title="Covered family" onClose={onClose} width={620}>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>Record the family each member's cover extends to. A new person is <strong style={{ color: "#b5651d" }}>in a {waitingDays}-day waiting period</strong> from the date added (shown in orange) before a full payout applies. Each member may cover up to <strong>{limit}</strong> {limit === 1 ? "person" : "people"}.</div>
      <label style={lbl}>Member — {mine.length} of {limit} covered</label>
      <select value={memberKey} onChange={(e) => setMemberKey(e.target.value)} style={{ ...inp, cursor: "pointer" }}>
        {memberEntries.map(([k, m]) => <option key={k} value={k}>{m.name || m.email || k}</option>)}
      </select>

      <div style={{ ...card, marginTop: 12, padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#f4f2ed", color: "#7a7568", textAlign: "left" }}><th style={{ padding: "7px 10px" }}>Covered person</th><th style={{ padding: "7px 10px" }}>Relationship</th><th style={{ padding: "7px 10px" }}>Added</th><th style={{ padding: "7px 10px" }}>Status</th>{canRecord && <th />}</tr></thead>
          <tbody>
            {mine.length === 0 ? <tr><td colSpan={5} style={{ padding: "10px", color: C.muted }}>No covered family recorded for this member yet.</td></tr> :
              mine.map((d) => {
                const s = coverStatus(d.coveredFrom, waitingDays, todayIso());
                return (
                <tr key={d.id} style={{ borderTop: "1px solid #eceae4" }}>
                  <td style={{ padding: "7px 10px", fontWeight: 600, color: s.active ? C.navy : "#b5651d" }}>{d.name}</td>
                  <td style={{ padding: "7px 10px" }}>{relationshipLabel(d.relationship)}</td>
                  <td style={{ padding: "7px 10px" }}>{d.coveredFrom || "—"}</td>
                  <td style={{ padding: "7px 10px" }}><CoverBadge dep={d} waitingDays={waitingDays} /></td>
                  {canRecord && <td style={{ padding: "7px 10px", textAlign: "right" }}><button onClick={() => { if (window.confirm("Remove " + d.name + " from covered family?")) run(() => deleteDependant(activeId, d.id)); }} style={{ background: "none", border: "1px solid #e8cccc", color: C.red, borderRadius: 4, padding: "2px 8px", fontSize: 11, cursor: "pointer" }}>Remove</button></td>}
                </tr>
              );})}
          </tbody>
        </table>
      </div>

      {canRecord && (
        <div style={{ marginTop: 12, borderTop: "1px solid " + C.line, paddingTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.navy, marginBottom: 4 }}>Add a covered family member</div>
          {atLimit
            ? <div style={{ fontSize: 12, color: "#b5651d", background: "#fff4e6", border: "1px solid #f0d0a0", borderRadius: 8, padding: "8px 11px" }}>This member is at the society's limit of {limit}. Remove someone, or raise the limit in <strong>Settings</strong>.</div>
            : <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr auto", gap: 8, alignItems: "end" }}>
                <div><label style={lbl}>Full name</label><input value={name} onChange={(e) => setName(e.target.value)} style={inp} placeholder="e.g. Nomsa Dlamini" /></div>
                <div><label style={lbl}>Relationship</label><select value={relationship} onChange={(e) => setRelationship(e.target.value)} style={{ ...inp, cursor: "pointer" }}>{BURIAL_RELATIONSHIPS.filter((r) => r.id !== "self").map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}</select></div>
                <div><label style={lbl}>Date of birth</label><input type="date" value={dob} onChange={(e) => setDob(e.target.value)} style={inp} /></div>
                <Btn kind="green" onClick={add}>Add</Btn>
              </div>}
          <div style={{ fontSize: 10.5, color: C.muted, marginTop: 6 }}>Cover starts today; the {waitingDays}-day waiting period is measured from now.</div>
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}><Btn kind="ghost" onClick={onClose}>Done</Btn></div>
    </Modal>
  );
}

function AddClaimModal({ stokvel, dependants, activeId, lockMemberKey, onClose, onSubmit }) {
  const memberEntries = Object.entries(stokvel.members || {});
  const [memberKey, setMemberKey] = useState(lockMemberKey || (memberEntries[0] ? memberEntries[0][0] : ""));
  const [selected, setSelected] = useState({});     // { optionId: true } — one or more covered people
  const [otherOn, setOtherOn] = useState(false);
  const [otherName, setOtherName] = useState("");
  const [otherRel, setOtherRel] = useState("other");
  const [claimDate, setClaimDate] = useState(todayIso());
  const [coverAmount, setCoverAmount] = useState("");
  const [coverTouched, setCoverTouched] = useState(false);
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");

  const member = stokvel.members[memberKey] || {};
  const mine = (dependants || []).filter((d) => d.memberKey === memberKey);
  const waitingDays = waitingDaysOf(stokvel);
  // Selectable people = the member themselves + everyone on their covered list.
  const options = [{ id: "self", name: member.name || member.email || "The member", relationship: "self", coveredFrom: null }]
    .concat(mine.map((d) => ({ id: d.id, name: d.name, relationship: d.relationship, coveredFrom: d.coveredFrom })));
  const chosen = options.filter((o) => selected[o.id]);
  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  // Reset the selection whenever the claimant changes.
  React.useEffect(() => { setSelected({}); }, [memberKey]);
  // Auto-fill cover = sum of each selected person's cover-by-relationship, until edited.
  React.useEffect(() => {
    if (coverTouched) return;
    const ca = stokvel.coverAmounts || {};
    let sum = 0;
    chosen.forEach((o) => { sum += Number(ca[o.relationship] || 0); });
    if (otherOn) sum += Number(ca[otherRel] || 0);
    if (sum > 0) setCoverAmount(String(sum));
  }, [JSON.stringify(selected), otherOn, otherRel]); // eslint-disable-line

  const submit = async () => {
    if (!memberKey) return setStatus("Choose the member.");
    const names = chosen.map((o) => o.name);
    if (otherOn) { if (!otherName.trim()) return setStatus("Enter the name of the other person."); names.push(otherName.trim()); }
    if (!names.length) return setStatus("Select at least one covered person.");
    const count = chosen.length + (otherOn ? 1 : 0);
    const relationship = count > 1 ? "multiple" : (otherOn ? otherRel : (chosen[0] ? chosen[0].relationship : "other"));
    setBusy(true); setStatus("");
    let certificate = null;
    try {
      if (file) { setStatus("Uploading certificate…"); certificate = await uploadReceipt(activeId, file); }
    } catch (e) { setBusy(false); return setStatus(e.message || "Could not upload the certificate."); }
    onSubmit({
      memberUid: memberKey.startsWith("tracked:") ? null : memberKey,
      memberName: member.name || member.email || "Member",
      reference: member.reference || "",
      deceasedName: names.join(", "), relationship, claimDate,
      coverAmount: Number(coverAmount) || 0,
      note: note.trim(), certificate,
    });
  };

  return (
    <Modal title="Log a burial claim" onClose={onClose} width={520}>
      {!lockMemberKey ? (<>
        <label style={lbl}>Member claiming</label>
        <select value={memberKey} onChange={(e) => setMemberKey(e.target.value)} style={{ ...inp, cursor: "pointer" }}>
          {memberEntries.map(([k, m]) => <option key={k} value={k}>{m.name || m.email || k}</option>)}
        </select>
      </>) : <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>Lodging a claim as <strong style={{ color: C.navy }}>{member.name || member.email}</strong>. The committee will review it.</div>}

      <label style={lbl}>Who passed away — select one or more covered people</label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4, border: "1px solid " + C.line, borderRadius: 8, padding: 8 }}>
        {options.map((o) => {
          const s = o.coveredFrom ? coverStatus(o.coveredFrom, waitingDays, claimDate) : { active: true };
          return (
            <label key={o.id} style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 5, cursor: "pointer", background: selected[o.id] ? "#eef6f0" : "#fff", border: "1px solid " + (selected[o.id] ? "#cfe3d6" : C.line), borderRadius: 6, padding: "4px 9px", color: s.active ? C.navy : "#b5651d" }}>
              <input type="checkbox" checked={!!selected[o.id]} onChange={() => toggle(o.id)} />
              {o.name} <span style={{ color: C.muted }}>· {relationshipLabel(o.relationship)}</span>{s.active ? "" : " · in waiting"}
            </label>
          );
        })}
      </div>
      <label style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 6, marginTop: 8, cursor: "pointer", color: C.navy }}>
        <input type="checkbox" checked={otherOn} onChange={(e) => setOtherOn(e.target.checked)} /> Someone not on the covered list
      </label>
      {otherOn && (
        <div style={{ marginTop: 6, background: "#fff7e6", border: "1px solid #f0d97f", borderRadius: 8, padding: "8px 11px", display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 8 }}>
          <div><label style={lbl}>Name</label><input value={otherName} onChange={(e) => setOtherName(e.target.value)} style={inp} placeholder="Full name" /></div>
          <div><label style={lbl}>Relationship</label><select value={otherRel} onChange={(e) => setOtherRel(e.target.value)} style={{ ...inp, cursor: "pointer" }}>{BURIAL_RELATIONSHIPS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}</select></div>
        </div>
      )}

      {chosen.some((o) => o.coveredFrom && !coverStatus(o.coveredFrom, waitingDays, claimDate).active) && (
        <div style={{ marginTop: 8, background: "#fff4e6", border: "1px solid #f0c98a", borderRadius: 8, padding: "8px 11px", fontSize: 11, color: "#b5651d" }}>⚠️ One or more selected people are still in the waiting period on this date — those may not qualify for a full payout.</div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
        <div><label style={lbl}>Date lodged</label><input type="date" value={claimDate} onChange={(e) => setClaimDate(e.target.value)} style={inp} /></div>
        <div><label style={lbl}>Cover amount (R)</label><input type="number" value={coverAmount} onChange={(e) => { setCoverTouched(true); setCoverAmount(e.target.value); }} style={inp} placeholder="auto from rules" /></div>
      </div>

      <label style={lbl}>Death certificate (PDF or photo — optional)</label>
      <input type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files && e.target.files[0])} style={{ fontSize: 11, marginTop: 4 }} />
      {file && <span style={{ fontSize: 11, color: C.green, marginLeft: 8 }}>✓ {file.name}</span>}

      <label style={lbl}>Note (optional)</label>
      <input value={note} onChange={(e) => setNote(e.target.value)} style={inp} placeholder="Any detail the society should record" />

      {status && <div style={{ marginTop: 10, fontSize: 12, color: busy ? C.muted : C.red }}>{status}</div>}
      <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
        <Btn kind="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
        <Btn kind="primary" onClick={submit} disabled={busy}>{busy ? "Saving…" : "Lodge claim"}</Btn>
      </div>
    </Modal>
  );
}

function PayClaimModal({ claim, onClose, onSubmit }) {
  const [amount, setAmount] = useState(String(claim.coverAmount || ""));
  const [date, setDate] = useState(todayIso());
  const submit = () => {
    if (!(Number(amount) > 0)) return alert("Enter the amount paid.");
    onSubmit({ amount: Number(amount), date });
  };
  return (
    <Modal title="Pay burial claim" onClose={onClose} width={420}>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>Paying <strong style={{ color: C.navy }}>{claim.memberName}</strong> for <strong style={{ color: C.navy }}>{claim.deceasedName}</strong> ({relationshipLabel(claim.relationship)}). This records a payout from the group's own account.</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div><label style={lbl}>Amount paid (R)</label><input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} style={inp} /></div>
        <div><label style={lbl}>Date paid</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inp} /></div>
      </div>
      <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
        <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
        <Btn kind="green" onClick={submit}>Record payment</Btn>
      </div>
    </Modal>
  );
}

function ClaimsTab({ data, canManageClaims, canManageFamily, myUid, activeId, run }) {
  const { stokvel, claims = [], dependants = [] } = data;
  const [showAdd, setShowAdd] = useState(false);
  const [showSelf, setShowSelf] = useState(false);
  const [showFamily, setShowFamily] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const iAmMember = !!(stokvel.members && stokvel.members[myUid]);
  const sorted = [...(claims || [])].sort((a, b) => String(b.claimDate).localeCompare(String(a.claimDate)));
  const coveredCount = (dependants || []).length;
  const memberEntries = Object.entries(stokvel.members || {});
  const memberN = memberEntries.length;
  const limit = coveredLimitOf(stokvel);
  const waitingDays = waitingDaysOf(stokvel);

  return (
    <div>
      {/* Covered-family register — visible to EVERY member (transparency). Orange = still in the waiting period. */}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>Covered family</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{coveredCount} covered across {memberN} member{memberN === 1 ? "" : "s"} · up to {limit} per member · {waitingDays}-day waiting period (<span style={{ color: "#b5651d", fontWeight: 700 }}>orange</span> = still in waiting).</div>
          </div>
          {canManageFamily && <Btn kind="ghost" onClick={() => setShowFamily(true)}>Manage</Btn>}
        </div>
        {memberEntries.map(([k, m]) => {
          const mine = (dependants || []).filter((d) => d.memberKey === k);
          return (
            <div key={k} style={{ borderTop: "1px solid #eceae4", padding: "8px 0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: C.navy }}>{m.name || m.email || k}</span>
                <span style={{ fontSize: 11, color: (limit > 0 && mine.length >= limit) ? "#b5651d" : C.muted }}>{mine.length} of {limit} covered</span>
              </div>
              {mine.length === 0 ? <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>No covered family recorded.</div> :
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 5 }}>
                  {mine.map((d) => {
                    const s = coverStatus(d.coveredFrom, waitingDays, todayIso());
                    return <span key={d.id} title={"Added " + (d.coveredFrom || "?")} style={{ fontSize: 11, borderRadius: 6, padding: "3px 8px", border: "1px solid " + (s.active ? C.line : "#f0c98a"), background: s.active ? "#fff" : "#ffe8cc", color: s.active ? C.navy : "#b5651d" }}>{d.name} · {relationshipLabel(d.relationship)}{s.active ? "" : " · waiting → " + s.waitingUntil}</span>;
                  })}
                </div>}
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ fontSize: 13, color: C.muted }}>Burial claims ({claims.length})</div>
        {canManageClaims ? <Btn kind="primary" onClick={() => setShowAdd(true)}>+ Log a claim</Btn>
          : iAmMember ? <Btn kind="primary" onClick={() => setShowSelf(true)}>Lodge my claim</Btn> : null}
      </div>

      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#f4f2ed", color: "#7a7568", textAlign: "left" }}>
            <th style={{ padding: "8px 10px" }}>Deceased</th><th style={{ padding: "8px 10px" }}>Member</th><th style={{ padding: "8px 10px" }}>Lodged</th><th style={{ padding: "8px 10px", textAlign: "right" }}>Cover</th><th style={{ padding: "8px 10px" }}>Cert.</th><th style={{ padding: "8px 10px" }}>Status</th>{canManageClaims && <th style={{ padding: "8px 10px" }} />}
          </tr></thead>
          <tbody>
            {sorted.length === 0 ? <tr><td colSpan={7} style={{ padding: 14, color: C.muted }}>No claims yet. Log one when a covered person passes away.</td></tr> :
              sorted.map((c) => (
                <tr key={c.id} style={{ borderTop: "1px solid #eceae4" }}>
                  <td style={{ padding: "8px 10px", fontWeight: 600, color: C.navy }}>{c.deceasedName}<div style={{ fontSize: 10, color: C.muted, fontWeight: 400 }}>{relationshipLabel(c.relationship)}</div></td>
                  <td style={{ padding: "8px 10px" }}>{c.memberName}</td>
                  <td style={{ padding: "8px 10px" }}>{c.claimDate}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600 }}>{money(c.coverAmount)}{c.status === "paid" && Number(c.amountPaid) !== Number(c.coverAmount) ? <div style={{ fontSize: 10, color: C.muted, fontWeight: 400 }}>paid {money(c.amountPaid)}</div> : null}</td>
                  <td style={{ padding: "8px 10px" }}><DocLink doc={c.certificate} /></td>
                  <td style={{ padding: "8px 10px" }}><Badge tone={claimStatusTone(c.status)}>{claimStatusLabel(c.status)}</Badge>{c.status === "paid" && c.datePaid ? <div style={{ fontSize: 10, color: C.muted }}>{c.datePaid}</div> : null}</td>
                  {canManageClaims && <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                    {c.status === "lodged" && <button onClick={() => run(() => updateClaim(activeId, c.id, { status: "approved" }))} style={miniBtn(C.green)}>Approve</button>}
                    {(c.status === "lodged" || c.status === "approved") && <button onClick={() => setPayFor(c)} style={miniBtn(C.navy)}>Pay</button>}
                    {(c.status === "lodged" || c.status === "approved") && <button onClick={() => { if (window.confirm("Reject this claim?")) run(() => updateClaim(activeId, c.id, { status: "rejected" })); }} style={miniBtn("#a33", true)}>Reject</button>}
                    <button onClick={() => { if (window.confirm("Delete this claim record?")) run(() => deleteClaim(activeId, c.id)); }} style={miniBtn("#b33", true)}>Delete</button>
                  </td>}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {showFamily && <CoveredFamilyModal stokvel={stokvel} dependants={dependants} canRecord={canManageFamily} activeId={activeId} run={run} onClose={() => setShowFamily(false)} />}
      {showAdd && <AddClaimModal stokvel={stokvel} dependants={dependants} activeId={activeId} onClose={() => setShowAdd(false)} onSubmit={(c) => { setShowAdd(false); run(() => addClaim(activeId, c)); }} />}
      {showSelf && <AddClaimModal stokvel={stokvel} dependants={dependants} activeId={activeId} lockMemberKey={myUid} onClose={() => setShowSelf(false)} onSubmit={(c) => { setShowSelf(false); run(() => addClaim(activeId, c)); }} />}
      {payFor && <PayClaimModal claim={payFor} onClose={() => setPayFor(null)} onSubmit={({ amount, date }) => { const c = payFor; setPayFor(null); run(() => payClaim(activeId, c.id, { amount, date })); }} />}
    </div>
  );
}

function miniBtn(color, outline) {
  return { marginLeft: 6, background: outline ? "none" : color, color: outline ? color : "#fff", border: "1px solid " + (outline ? "#e0ddd8" : color), borderRadius: 4, padding: "3px 9px", fontSize: 11, cursor: "pointer" };
}

// ───────────────────────────── Governance: constitution + minutes ─────────────────────────────

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => { const s = String(r.result || ""); const c = s.indexOf(","); resolve(c >= 0 ? s.slice(c + 1) : s); };
    r.onerror = () => reject(new Error("Could not read the file."));
    r.readAsDataURL(file);
  });
}

// Render the current constitution to a real, downloadable PDF stamped with its version.
function downloadConstitutionPdf(stokvel) {
  const version = stokvel.constitutionVersion || 1;
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const margin = 48;
  const pageH = pdf.internal.pageSize.getHeight();
  const width = pdf.internal.pageSize.getWidth() - margin * 2;
  let y = margin;
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(16); pdf.setTextColor(26, 26, 46);
  pdf.text(String(stokvel.name || "Stokvel") + " — Constitution", margin, y); y += 22;
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(10); pdf.setTextColor(120, 120, 120);
  pdf.text("Version " + version + "   ·   Generated " + new Date().toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" }), margin, y); y += 26;
  pdf.setTextColor(30, 30, 30); pdf.setFontSize(11);
  const lines = pdf.splitTextToSize(String(stokvel.constitutionText || ""), width);
  lines.forEach((ln) => { if (y > pageH - margin) { pdf.addPage(); y = margin; } pdf.text(ln, margin, y); y += 15; });
  pdf.save(String(stokvel.name || "stokvel").replace(/[^a-z0-9]+/gi, "-").toLowerCase() + "-constitution-v" + version + ".pdf");
}

function downloadMinutesPdf(minute, stokvel) {
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const margin = 48; const pageH = pdf.internal.pageSize.getHeight(); const width = pdf.internal.pageSize.getWidth() - margin * 2;
  let y = margin;
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(15); pdf.setTextColor(26, 26, 46);
  pdf.text(String(stokvel.name || "Stokvel") + " — Minutes", margin, y); y += 20;
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(11); pdf.setTextColor(60, 60, 60);
  pdf.text((minute.title || "Meeting") + "   ·   " + (minute.date || "") + (minute.time ? " " + minute.time : ""), margin, y); y += 22;
  pdf.setTextColor(30, 30, 30); pdf.setFontSize(11);
  pdf.splitTextToSize(String(minute.body || ""), width).forEach((ln) => { if (y > pageH - margin) { pdf.addPage(); y = margin; } pdf.text(ln, margin, y); y += 15; });
  pdf.save(String(stokvel.name || "stokvel").replace(/[^a-z0-9]+/gi, "-").toLowerCase() + "-minutes-" + (minute.date || "") + ".pdf");
}

// Guided minute-taker: header + attendance checklist + searchable "who spoke" log +
// next meeting — so the minutes are complete and saved by the end of the meeting.
function MinuteModal({ stokvel, activeId, byName, onClose, onSaved }) {
  const members = Object.entries(stokvel.members || {}).map(([k, m]) => ({ key: k, name: m.name || m.email || k }));
  const [title, setTitle] = useState("Meeting");
  const [date, setDate] = useState(todayIso());
  const [time, setTime] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [nextTime, setNextTime] = useState("");
  const [present, setPresent] = useState(() => { const o = {}; members.forEach((m) => { o[m.key] = true; }); return o; });
  const [guests, setGuests] = useState("");
  const [discussion, setDiscussion] = useState([]);
  const [spk, setSpk] = useState("");
  const [pt, setPt] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState("");

  const toggle = (k) => setPresent((s) => ({ ...s, [k]: !s[k] }));
  const addPoint = () => { if (!spk.trim() && !pt.trim()) return; setDiscussion((d) => [...d, { speaker: spk.trim(), point: pt.trim() }]); setSpk(""); setPt(""); };
  const removePoint = (i) => setDiscussion((d) => d.filter((_, idx) => idx !== i));

  const buildBody = () => {
    const presentNames = members.filter((m) => present[m.key]).map((m) => m.name);
    const absentNames = members.filter((m) => !present[m.key]).map((m) => m.name);
    const L = [];
    if (time) L.push("Time: " + time);
    L.push("Present: " + (presentNames.join(", ") || "—"));
    if (absentNames.length) L.push("Apologies / absent: " + absentNames.join(", "));
    if (guests.trim()) L.push("Guests: " + guests.trim());
    L.push("");
    if (discussion.length) { L.push("Discussion:"); discussion.forEach((d) => L.push("• " + (d.speaker ? d.speaker + ": " : "") + d.point)); L.push(""); }
    if (notes.trim()) { L.push(notes.trim()); L.push(""); }
    if (nextDate) L.push("Next meeting: " + nextDate + (nextTime ? " at " + nextTime : ""));
    return L.join("\n").trim();
  };

  const submit = async () => {
    setBusy("Saving…");
    const presentNames = members.filter((m) => present[m.key]).map((m) => m.name);
    const absentNames = members.filter((m) => !present[m.key]).map((m) => m.name);
    let attachment = null;
    try {
      if (file) { setBusy("Uploading attachment…"); attachment = await uploadReceipt(activeId, file); }
      await addMinute(activeId, { title: title.trim(), date, time, attendees: presentNames.join(", "), absentees: absentNames.join(", "), guests: guests.trim(), nextMeeting: nextDate ? (nextDate + (nextTime ? " " + nextTime : "")) : "", discussion, notes: notes.trim(), body: buildBody(), attachment, recordedByName: byName });
      onSaved();
    } catch (e) { setBusy(e.message || "Could not save."); }
  };

  return (
    <Modal title="Take meeting minutes" onClose={onClose} width={640}>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10 }}>
        <div><label style={lbl}>Title</label><input style={inp} value={title} onChange={(e) => setTitle(e.target.value)} /></div>
        <div><label style={lbl}>Date</label><input style={inp} type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
        <div><label style={lbl}>Time</label><input style={inp} type="time" value={time} onChange={(e) => setTime(e.target.value)} /></div>
      </div>

      <label style={lbl}>Attendance — tick who is present</label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4, maxHeight: 130, overflowY: "auto", border: "1px solid " + C.line, borderRadius: 8, padding: 8 }}>
        {members.length === 0 ? <span style={{ fontSize: 11, color: C.muted }}>No members yet.</span> :
          members.map((m) => (
            <label key={m.key} style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 4, cursor: "pointer", background: present[m.key] ? "#eef6f0" : "#fff", border: "1px solid " + (present[m.key] ? "#cfe3d6" : C.line), borderRadius: 6, padding: "3px 8px" }}>
              <input type="checkbox" checked={!!present[m.key]} onChange={() => toggle(m.key)} />{m.name}
            </label>
          ))}
      </div>
      <label style={lbl}>Guests (optional)</label><input style={inp} value={guests} onChange={(e) => setGuests(e.target.value)} placeholder="Non-member names, comma separated" />

      <label style={lbl}>Discussion — who spoke &amp; what they said</label>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr auto", gap: 8, alignItems: "end" }}>
        <div>
          <input list="min-members" value={spk} onChange={(e) => setSpk(e.target.value)} placeholder="Search speaker…" style={inp} />
          <datalist id="min-members">{members.map((m) => <option key={m.key} value={m.name} />)}</datalist>
        </div>
        <input value={pt} onChange={(e) => setPt(e.target.value)} placeholder="What they said / the point raised" style={inp} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPoint(); } }} />
        <Btn kind="green" onClick={addPoint}>Add</Btn>
      </div>
      {discussion.length > 0 && (
        <div style={{ ...card, marginTop: 8, padding: "8px 12px" }}>
          {discussion.map((d, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12, padding: "3px 0", borderTop: i ? "1px solid #eceae4" : "none" }}>
              <span><strong style={{ color: C.navy }}>{d.speaker || "—"}:</strong> {d.point}</span>
              <button onClick={() => removePoint(i)} style={{ background: "none", border: "none", color: C.red, cursor: "pointer", fontSize: 12 }}>✕</button>
            </div>
          ))}
        </div>
      )}

      <label style={lbl}>Other notes / resolutions (optional)</label>
      <textarea style={{ ...inp, minHeight: 70, fontFamily: "inherit", resize: "vertical" }} value={notes} onChange={(e) => setNotes(e.target.value)} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div><label style={lbl}>Next meeting — date</label><input style={inp} type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} /></div>
        <div><label style={lbl}>Next meeting — time</label><input style={inp} type="time" value={nextTime} onChange={(e) => setNextTime(e.target.value)} /></div>
      </div>

      <label style={lbl}>Attachment (optional)</label><input type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files && e.target.files[0])} style={{ fontSize: 11, marginTop: 4 }} />{file && <span style={{ fontSize: 11, color: C.green, marginLeft: 8 }}>✓ {file.name}</span>}

      {busy && <div style={{ marginTop: 10, fontSize: 12, color: C.muted }}>{busy}</div>}
      <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
        <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
        <Btn kind="green" onClick={submit}>Save minutes</Btn>
      </div>
    </Modal>
  );
}

function GovernanceTab({ data, canGovern, activeId, run, authUser }) {
  const { stokvel } = data;
  const [text, setText] = useState(stokvel.constitutionText || "");
  const [importing, setImporting] = useState("");
  const [history, setHistory] = useState([]);
  const [minutes, setMinutes] = useState([]);
  const [showMinute, setShowMinute] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const myName = (authUser && authUser.email) || "a member";
  const version = stokvel.constitutionVersion || 0;
  const [docs, setDocs] = useState([]);
  const [docHeading, setDocHeading] = useState("");
  const [docBusy, setDocBusy] = useState("");

  const reloadMinutes = () => loadMinutes(activeId).then(setMinutes).catch(() => {});
  const reloadDocs = () => loadStokvelDocs(activeId).then(setDocs).catch(() => {});
  React.useEffect(() => { loadConstitutionHistory(activeId).then(setHistory).catch(() => {}); reloadMinutes(); reloadDocs(); }, [activeId, stokvel.constitutionVersion]); // eslint-disable-line

  const saveDoc = async (file) => {
    if (!file || !docHeading.trim()) return;
    setDocBusy("Uploading…");
    try {
      const meta = await uploadReceipt(activeId, file);
      await addStokvelDoc(activeId, { heading: docHeading.trim(), file: meta, byName: myName });
      setDocHeading(""); setDocBusy(""); reloadDocs();
    } catch (e) { setDocBusy(e.message || "Upload failed"); }
  };
  React.useEffect(() => { setText(stokvel.constitutionText || ""); }, [stokvel.constitutionVersion]); // eslint-disable-line

  const importPdf = async (file) => {
    if (!file) return;
    setImporting("Reading the PDF…");
    try { const b64 = await fileToBase64(file); const { text: t } = await extractPdfText(b64); if (t && t.trim()) { setText(t); setImporting("Imported — review/edit, then save it as a version."); } else setImporting("No readable text found in that PDF."); }
    catch (e) { setImporting(e.message || "Could not read the PDF."); }
  };
  const saveVersion = () => {
    if (!text.trim()) return alert("The constitution is empty.");
    const summary = window.prompt("Briefly describe this amendment (what changed or was added?):", "");
    if (summary === null) return;
    run(async () => { await saveConstitutionVersion(activeId, text, summary, myName); });
  };
  const changed = text !== (stokvel.constitutionText || "");

  return (
    <div>
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>Constitution {version ? "· Version " + version : "· not set up yet"}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{stokvel.constitutionUpdatedBy ? "Last amended by " + stokvel.constitutionUpdatedBy : "The living rulebook — amend it here; each save is a new version, recorded with who and when."}</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {version > 0 && <Btn kind="ghost" onClick={() => downloadConstitutionPdf(stokvel)}>Download PDF</Btn>}
            {history.length > 0 && <Btn kind="ghost" onClick={() => setShowHistory((s) => !s)}>{showHistory ? "Hide history" : "History"}</Btn>}
          </div>
        </div>

        {showHistory && (
          <div style={{ ...card, marginTop: 10, background: "#faf9f6" }}>
            {history.map((h) => (
              <div key={h.id} style={{ borderTop: "1px solid #eceae4", padding: "6px 0", fontSize: 12 }}>
                <div style={{ color: C.navy, fontWeight: 600 }}>Version {h.version}{h.summary ? " — " + h.summary : ""}</div>
                <div style={{ fontSize: 10.5, color: C.muted }}>{h.changedByName || "—"}{h.at && h.at.toDate ? " · " + h.at.toDate().toLocaleString("en-ZA", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</div>
              </div>
            ))}
          </div>
        )}

        {canGovern ? (
          <div style={{ marginTop: 10 }}>
            <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Type or paste the constitution here, one clause per paragraph…" style={{ ...inp, minHeight: 300, fontFamily: "inherit", lineHeight: 1.6, resize: "vertical" }} />
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
              <Btn kind="primary" onClick={saveVersion} disabled={!changed}>{version ? "Save as version " + (version + 1) : "Save version 1"}</Btn>
              <label style={{ fontSize: 12, color: C.navy, border: "1px solid " + C.line, borderRadius: 7, padding: "8px 12px", cursor: "pointer", background: "#fff" }}>
                Import text from a PDF
                <input type="file" accept=".pdf" style={{ display: "none" }} onChange={(e) => importPdf(e.target.files && e.target.files[0])} />
              </label>
              <select defaultValue="" onChange={(e) => { const id = e.target.value; e.target.value = ""; if (!id) return; if (!text.trim() || window.confirm("Replace the current text with this template? You can then edit it before saving.")) setText(constitutionTemplate(id, stokvel.name)); }} style={{ fontSize: 12, padding: "8px 10px", borderRadius: 7, border: "1px solid " + C.line, background: "#fff", cursor: "pointer", color: C.navy }}>
                <option value="">Start from a template…</option>
                {CONSTITUTION_TEMPLATES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              {importing && <span style={{ fontSize: 11, color: C.muted }}>{importing}</span>}
              {changed && !importing && <span style={{ fontSize: 11, color: "#8a6d1d" }}>Unsaved changes</span>}
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 10 }}>
            {stokvel.constitutionText
              ? <div style={{ whiteSpace: "pre-wrap", fontSize: 13, lineHeight: 1.6, color: C.ink, background: "#faf9f6", border: "1px solid " + C.line, borderRadius: 8, padding: 14, maxHeight: 420, overflowY: "auto" }}>{stokvel.constitutionText}</div>
              : <div style={{ fontSize: 12, color: C.muted }}>No constitution captured yet.</div>}
          </div>
        )}
      </div>

      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>Meeting minutes</div>
          {canGovern && <Btn kind="primary" onClick={() => setShowMinute(true)}>+ Save minutes</Btn>}
        </div>
        {minutes.length === 0 ? <div style={{ fontSize: 12, color: C.muted }}>No minutes saved yet.</div> :
          minutes.map((m) => (
            <div key={m.id} style={{ borderTop: "1px solid #eceae4", padding: "8px 0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>{m.title || "Meeting"} <span style={{ fontSize: 11, fontWeight: 400, color: C.muted }}>· {m.date}</span></div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {m.attachment && <DocLink doc={m.attachment} />}
                  <button onClick={() => downloadMinutesPdf(m, stokvel)} style={{ background: "none", border: "1px solid " + C.line, color: C.navy, borderRadius: 4, padding: "2px 8px", fontSize: 11, cursor: "pointer", fontWeight: 600 }}>PDF</button>
                  {canGovern && <button onClick={() => { if (window.confirm("Delete these minutes?")) deleteMinute(activeId, m.id).then(reloadMinutes); }} style={{ background: "none", border: "1px solid #e8cccc", color: C.red, borderRadius: 4, padding: "2px 8px", fontSize: 11, cursor: "pointer" }}>Delete</button>}
                </div>
              </div>
              {m.attendees ? <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Present: {m.attendees}</div> : null}
              {m.body ? <div style={{ whiteSpace: "pre-wrap", fontSize: 12, color: C.ink, marginTop: 5, lineHeight: 1.5 }}>{m.body}</div> : null}
              <div style={{ fontSize: 10, color: "#b0aa9e", marginTop: 4 }}>Recorded by {m.recordedByName || "—"}</div>
            </div>
          ))}
      </div>

      <div style={card}>
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>Other documents</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Any other group document — give it a heading and upload it (e.g. AGM resolution, bank mandate, membership register). Visible to all members.</div>
        </div>
        {canGovern && (
          <div style={{ background: "#faf9f6", border: "1px solid " + C.line, borderRadius: 8, padding: 12, marginBottom: 10 }}>
            <label style={lbl}>Document heading</label>
            <input style={inp} value={docHeading} onChange={(e) => setDocHeading(e.target.value)} placeholder="e.g. 2026 AGM Resolution" />
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
              <label style={{ fontSize: 12, color: C.navy, border: "1px solid " + C.line, borderRadius: 7, padding: "8px 12px", cursor: docHeading.trim() && !docBusy ? "pointer" : "not-allowed", background: "#fff", opacity: docHeading.trim() && !docBusy ? 1 : 0.5 }}>
                Choose file & save (PDF or photo)
                <input type="file" accept=".pdf,image/*" disabled={!docHeading.trim() || !!docBusy} style={{ display: "none" }} onChange={(e) => saveDoc(e.target.files && e.target.files[0])} />
              </label>
              {docBusy && <span style={{ fontSize: 11, color: C.muted }}>{docBusy}</span>}
              {!docHeading.trim() && !docBusy && <span style={{ fontSize: 11, color: C.muted }}>Enter a heading first, then choose the file.</span>}
            </div>
          </div>
        )}
        {docs.length === 0 ? <div style={{ fontSize: 12, color: C.muted }}>No documents uploaded yet.</div> :
          docs.map((d) => (
            <div key={d.id} style={{ borderTop: "1px solid #eceae4", padding: "8px 0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>{d.heading || "Document"}</div>
                <div style={{ fontSize: 10, color: "#b0aa9e", marginTop: 2 }}>Uploaded by {d.uploadedByName || "—"}{d.at && d.at.toDate ? " · " + d.at.toDate().toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" }) : ""}</div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {d.file && <DocLink doc={d.file} />}
                {canGovern && <button onClick={() => { if (window.confirm("Delete this document?")) deleteStokvelDoc(activeId, d.id).then(reloadDocs); }} style={{ background: "none", border: "1px solid #e8cccc", color: C.red, borderRadius: 4, padding: "2px 8px", fontSize: 11, cursor: "pointer" }}>Delete</button>}
              </div>
            </div>
          ))}
      </div>

      {showMinute && <MinuteModal stokvel={stokvel} activeId={activeId} byName={myName} onClose={() => setShowMinute(false)} onSaved={() => { setShowMinute(false); reloadMinutes(); }} />}
    </div>
  );
}

// ───────────────────────────── Root view ─────────────────────────────

// Join an existing group with the chair's invite code. This does NOT make you a member
// straight away — it puts you in the group's waiting room until the chair approves you.
function JoinStokvelModal({ onClose, onJoined }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const submit = async () => {
    const c = code.trim();
    if (!c) return setStatus("Enter the invite code your chairperson gave you.");
    setBusy(true); setStatus("");
    try {
      const r = await joinStokvelByCode(c);
      if (r.status === "active") onJoined("You're already a member of " + (r.name || "that group") + ".");
      else onJoined("Request sent to " + (r.name || "the group") + " 🎉 — you'll get access once the chairperson approves you.");
    } catch (e) { setStatus(e.message || "Could not join."); setBusy(false); return; }
    setBusy(false);
  };
  return (
    <Modal title="Join a stokvel" onClose={onClose}>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>Enter the invite code your chairperson gave you. You'll be placed in the group's waiting room until they approve you.</div>
      <label style={lbl}>Invite code</label>
      <input style={{ ...inp, fontFamily: "monospace", textTransform: "uppercase" }} value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. MAS7QK" />
      {status && <div style={{ marginTop: 8, fontSize: 12, color: C.red }}>{status}</div>}
      <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
        <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
        <Btn kind="primary" onClick={submit} disabled={busy}>{busy ? "Sending…" : "Request to join"}</Btn>
      </div>
    </Modal>
  );
}

// ── Bank activity (member-visible transparency) ──
// Shows every transaction pulled from the group's own bank account — INCLUDING ones the
// treasurer hasn't classified yet — to EVERY member. This is the anti-fraud promise: no
// one can quietly sit on a transaction or hide a payment. "Recorded" is computed by
// linking each bank row's __bankTxnId to the ledger, so it stays accurate whether the
// chair reconciles by reference or classifies via import. Read-only for members; the
// chair gets connect/refresh controls (dormant until the Stitch feed is provisioned).
function BankActivityTab({ data, bankTxns, canRecord, bankLinked, syncing, syncMsg, onConnect, onRefresh }) {
  const { contributions, loans, payouts, repayments, expenses, income } = data;
  const recorded = new Set();
  [contributions, loans, payouts, repayments, expenses, income].forEach((list) =>
    (list || []).forEach((r) => { if (r && r.__bankTxnId) recorded.add(r.__bankTxnId); })
  );
  const isRecorded = (t) => t.status === "classified" || recorded.has(t.__bankTxnId);
  const rows = [...(bankTxns || [])].sort((a, b) => new Date(b.date) - new Date(a.date));
  const awaitingCount = rows.filter((t) => !isRecorded(t)).length;

  return (
    <div>
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>🏦 Bank activity</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4, maxWidth: 560, lineHeight: 1.5 }}>
              Straight from the group's own bank account — visible to <strong>every member</strong>,
              even before the treasurer files it. Nothing here can be hidden or quietly delayed.
            </div>
          </div>
          {canRecord && bankLinked && (
            <Btn kind="gold" onClick={onRefresh} disabled={syncing}>{syncing ? "Syncing…" : "↻ Refresh"}</Btn>
          )}
        </div>
        {syncMsg && <div style={{ fontSize: 12, color: C.ink, marginTop: 10 }}>{syncMsg}</div>}
        {!bankLinked && (
          <div style={{ fontSize: 12, color: C.ink, marginTop: 10, lineHeight: 1.6 }}>
            {canRecord ? (
              <>
                Link the group's <strong>own</strong> bank account (read-only) so every deposit and
                payment appears here automatically — no one has to upload anything.{" "}
                <Btn kind="primary" onClick={onConnect} disabled={!BANK_FEED_ENABLED} style={{ marginLeft: 6 }}>
                  + Connect bank (read-only)
                </Btn>
                {!BANK_FEED_ENABLED && <span style={{ color: C.muted, marginLeft: 8 }}>Coming soon.</span>}
              </>
            ) : (
              "The bank account isn't linked yet. Ask the chairperson to connect it for automatic, transparent records."
            )}
          </div>
        )}
      </div>

      {rows.length > 0 ? (
        <div style={card}>
          {awaitingCount > 0 && (
            <div style={{ background: "#fff3c4", border: "1px solid #f0d97f", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#8a6d1d", marginBottom: 12 }}>
              ⏳ {awaitingCount} transaction{awaitingCount === 1 ? "" : "s"} awaiting classification — every member can see {awaitingCount === 1 ? "it" : "them"} while the treasurer files {awaitingCount === 1 ? "it" : "them"}.
            </div>
          )}
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ color: C.muted, textAlign: "left" }}>
                <th style={{ padding: "4px 6px" }}>Date</th>
                <th style={{ padding: "4px 6px" }}>Description</th>
                <th style={{ padding: "4px 6px", textAlign: "right" }}>Amount</th>
                <th style={{ padding: "4px 6px" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t, i) => {
                const rec = isRecorded(t);
                const inbound = Number(t.amount) >= 0;
                return (
                  <tr key={t.__bankTxnId || i} style={{ borderTop: "1px solid #eceae4" }}>
                    <td style={{ padding: "6px" }}>{t.date}</td>
                    <td style={{ padding: "6px" }}>{t.description || "—"}</td>
                    <td style={{ padding: "6px", textAlign: "right", color: inbound ? C.green : C.red, fontWeight: 600 }}>
                      {inbound ? "+" : "−"}{money(Math.abs(Number(t.amount) || 0))}
                    </td>
                    <td style={{ padding: "6px" }}>
                      {rec ? <Badge tone="ok">✓ Recorded</Badge> : <Badge tone="warn">⏳ Awaiting classification</Badge>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        bankLinked && <div style={{ ...card, fontSize: 12, color: C.muted }}>No bank transactions synced yet. Tap Refresh to pull the latest.</div>
      )}
    </div>
  );
}

export function StokvelView({ authUser, refreshSignal }) {
  const [stokvels, setStokvels] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [data, setData] = useState(null); // { stokvel, contributions, payouts, loans, repayments }
  const [bankTxns, setBankTxns] = useState([]);
  const [bankCfg, setBankCfg] = useState(null);
  const [bankSyncing, setBankSyncing] = useState(false);
  const [bankSyncMsg, setBankSyncMsg] = useState("");
  const [tab, setTab] = useState("overview");
  const [memberDetail, setMemberDetail] = useState(null); // { member, focus }
  const openMemberDetail = (m, focus) => setMemberDetail({ member: resolveMemberObj(m, data && data.stokvel), focus: focus || "all" });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [amendTarget, setAmendTarget] = useState(null); // { kind, entry } — correction being proposed
  const [joinMsg, setJoinMsg] = useState("");
  const [pendingJoins, setPendingJoins] = useState([]); // groups I'm waiting to be approved into
  const [requests, setRequests] = useState([]);         // join requests I can approve (current group)
  const [err, setErr] = useState("");

  const refreshList = async () => {
    if (!authUser) return;
    setLoading(true);
    try {
      const list = await listStokvelsForUser(authUser.uid);
      setStokvels(list);
      if (!activeId && list.length) setActiveId(list[0].id);
      if (activeId && !list.some((s) => s.id === activeId)) setActiveId(list[0] ? list[0].id : null);
      try { setPendingJoins(await listMyPendingJoins(authUser.uid)); } catch (e) { /* non-fatal */ }
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  const reload = async () => {
    if (!activeId) { setData(null); setRequests([]); return; }
    try {
      const d = await loadStokvelData(activeId);
      setData(d);
      setBankTxns(await loadStokvelBankTransactions(activeId));
      if (BANK_FEED_ENABLED) { try { setBankCfg(await loadStokvelBankFeedConfig(activeId)); } catch (e) { setBankCfg(null); } }
      // Load pending join requests only if I'm allowed to approve them.
      try { setRequests(d.stokvel && memberCan(d.stokvel, authUser.uid, "approve") ? await listJoinRequests(activeId) : []); }
      catch (e) { setRequests([]); }
    } catch (e) { setErr(e.message); }
  };

  // Re-list when the user changes OR after they join a new group via invite code
  // (refreshSignal bumps), so the freshly-joined stokvel appears without a reload.
  useEffect(() => { refreshList(); /* eslint-disable-next-line */ }, [authUser, refreshSignal]);
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [activeId]);

  // Handle return from Paystack (?stokvelPay=verify|cancelled). There is no webhook —
  // on `verify` we confirm the stored payment reference server-side, which activates
  // the subscription, then reload so the fresh status shows.
  const [payMsg, setPayMsg] = useState("");
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const p = params.get("stokvelPay");
    if (!p) return;
    // Strip the marker from the URL immediately so a refresh doesn't re-run this.
    params.delete("stokvelPay");
    const url = window.location.pathname + (params.toString() ? "?" + params.toString() : "");
    window.history.replaceState({}, "", url);

    if (p === "cancelled") {
      clearPendingPaystack();
      setPayMsg("Payment was cancelled. You can subscribe any time.");
      return;
    }
    if (p !== "verify") return;

    // Paystack may append its own ?reference / ?trxref; prefer the one we stored.
    const stored = pendingPaystack();
    const reference = stored.reference || params.get("reference") || params.get("trxref");
    const sid = stored.stokvelId;
    if (!reference) {
      setPayMsg("We couldn't find your payment reference. If you were charged, please contact support@tlhakiso.co.za.");
      return;
    }
    setPayMsg("Confirming your payment…");
    let cancelled = false;
    verifyPaystack(sid, reference)
      .then(() => { if (cancelled) return; clearPendingPaystack(); setPayMsg("Payment received — thank you! Your subscription is now active."); reload(); })
      .catch((e) => { if (cancelled) return; setPayMsg(e.message || "We couldn't confirm your payment. If you were charged, contact support@tlhakiso.co.za."); });
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, []);

  const run = async (fn) => { setBusy(true); setErr(""); try { await fn(); await reload(); await refreshList(); } catch (e) { setErr(e.message); alert(e.message); } setBusy(false); };

  const myRole = data && data.stokvel && data.stokvel.members && authUser ? ((data.stokvel.members[authUser.uid] || {}).role || "member") : "member";
  const myUid = authUser ? authUser.uid : "";
  // WEB-ONLY soft paywall: recording money needs an active Paystack subscription on the
  // web. The mobile apps are free (no in-app purchase) and don't apply this. Settings,
  // members, covered-family and governance stay available so a group can be set up
  // (and viewed) before/without subscribing.
  const subActive = data && data.stokvel ? subscriptionState(data.stokvel).active : false;
  const canRecord = subActive && (data && data.stokvel ? memberCan(data.stokvel, myUid, "money") : false);
  const canManageClaims = subActive && (data && data.stokvel ? memberCan(data.stokvel, myUid, "claims") : false);
  const canManageFamily = data && data.stokvel ? memberCan(data.stokvel, myUid, "family") : false;
  const canManageSettings = data && data.stokvel ? memberCan(data.stokvel, myUid, "settings") : false;
  const canGovern = data && data.stokvel ? memberCan(data.stokvel, myUid, "governance") : false;
  const canApprove = data && data.stokvel ? memberCan(data.stokvel, myUid, "approve") : false;

  // ── Bank feed (read-only) — chair connect/refresh; dormant until Stitch provisioned ──
  const bankLinked = !!(bankCfg && bankCfg.linkId);
  const onConnectBank = async () => {
    if (!activeId) return;
    try {
      setBankSyncMsg("");
      try { sessionStorage.setItem("stitch_link_project", activeId); sessionStorage.setItem("stitch_link_parent", "stokvels"); } catch (e) {}
      await openConnectWidget({ state: activeId });
    } catch (e) { setBankSyncMsg(e.message || "Could not start bank linking."); }
  };
  const onRefreshBank = async () => {
    if (!activeId) return;
    setBankSyncing(true); setBankSyncMsg("");
    try {
      const added = await syncStokvelBankFeed(activeId);
      setBankSyncMsg(added ? (added + " new transaction(s) synced.") : "Up to date — no new transactions.");
      setBankTxns(await loadStokvelBankTransactions(activeId));
      try { setBankCfg(await loadStokvelBankFeedConfig(activeId)); } catch (e) { /* ignore */ }
    } catch (e) { setBankSyncMsg(e.message || "Sync failed."); }
    setBankSyncing(false);
  };

  if (loading && !stokvels.length) return <div style={{ padding: 40, color: C.muted }}>Loading stokvels…</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {data && data.stokvel && data.stokvel.logo && data.stokvel.logo.url
            ? <img src={data.stokvel.logo.thumbUrl || data.stokvel.logo.url} alt="logo" style={{ width: 42, height: 42, objectFit: "cover", borderRadius: 9, border: "1px solid " + C.line }} />
            : null}
          <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.navy }}>{data && data.stokvel ? data.stokvel.name : "Tlhakiso Stokvel"}</div>
          <div style={{ fontSize: 12, color: C.muted }}>Transparent, non-custodial savings-group records</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {stokvels.length > 0 && (
            <select value={activeId || ""} onChange={(e) => { setActiveId(e.target.value); setTab("overview"); }} style={{ padding: "8px 12px", borderRadius: 7, border: "1px solid #e0ddd8", fontSize: 13, background: "#fff", cursor: "pointer" }}>
              {stokvels.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          {data && myRole === "chair" && <Btn kind="gold" onClick={() => setShowInvite(true)}>Invite</Btn>}
          <Btn kind="ghost" onClick={() => setShowJoin(true)}>Join with code</Btn>
          <Btn kind="primary" onClick={() => setShowCreate(true)}>+ New stokvel</Btn>
        </div>
      </div>

      {joinMsg && <div style={{ background: "#eef6f0", border: "1px solid #cfe3d6", borderRadius: 8, padding: "9px 13px", marginBottom: 12, fontSize: 12.5, color: "#2f5c40", display: "flex", justifyContent: "space-between" }}><span>{joinMsg}</span><button onClick={() => setJoinMsg("")} style={{ background: "none", border: "none", color: "#2f5c40", cursor: "pointer" }}>✕</button></div>}

      {pendingJoins.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          {pendingJoins.map((p) => (
            <div key={p.stokvelId || p.id} style={{ background: "#fff8ec", border: "1px solid #f0c98a", borderRadius: 8, padding: "11px 14px", marginBottom: 8, fontSize: 13, color: "#8a6d1d" }}>
              ⏳ You've requested to join <strong>{p.stokvelName || "a stokvel"}</strong>. You'll get access once the chairperson approves you.
            </div>
          ))}
        </div>
      )}

      {err && <div style={{ background: "#fff4f4", color: C.red, fontSize: 12, padding: "8px 12px", borderRadius: 6, marginBottom: 12, border: "1px solid #f0c0c0" }}>{err}</div>}

      {stokvels.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: C.muted }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🤝</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#5a5a5a" }}>No stokvels yet</div>
          <div style={{ fontSize: 13, marginTop: 8, maxWidth: 440, marginLeft: "auto", marginRight: "auto" }}>Create a savings group to track contributions, loans, payouts and bank reconciliation — with full transparency for every member.</div>
          <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "center" }}>
            <Btn kind="primary" onClick={() => setShowCreate(true)}>+ Create a stokvel</Btn>
            <Btn kind="ghost" onClick={() => setShowJoin(true)}>Join with a code</Btn>
          </div>
        </div>
      ) : data ? (
        <div style={{ opacity: busy ? 0.6 : 1 }}>
          <Disclaimer />
          {payMsg && <div style={{ background: "#eef6f0", border: "1px solid #cfe3d6", borderRadius: 8, padding: "9px 13px", marginBottom: 12, fontSize: 12, color: "#2f5c40", display: "flex", justifyContent: "space-between" }}><span>{payMsg}</span><button onClick={() => setPayMsg("")} style={{ background: "none", border: "none", color: "#2f5c40", cursor: "pointer" }}>✕</button></div>}
          <SubscriptionBanner stokvel={data.stokvel} myRole={myRole} />
          <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
            {[["overview", "Overview"], ["members", "Members"], ["contributions", "Contributions"], ["loans", "Loans"], ["payouts", "Payouts"], ...(data.stokvel.type === "burial" ? [["claims", "Claims"]] : []), ["expenses", "Fees & Income"], ...((bankTxns.length > 0 || BANK_FEED_ENABLED) ? [["bankfeed", "Bank activity"]] : []), ["governance", "Governance"], ...(myRole === "chair" ? [["permissions", "Permissions"]] : []), ["settings", "Settings"]].map(([id, label]) => (
              <button key={id} onClick={() => setTab(id)} style={{ padding: "7px 14px", borderRadius: 7, border: "1px solid #e0ddd8", background: tab === id ? C.navy : "#fff", color: tab === id ? C.gold : C.navy, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{label}</button>
            ))}
          </div>

          <PendingAmendments amendments={data.amendments} myRole={myRole} myUid={myUid} canAct={canRecord} onConfirm={(aid) => run(() => confirmAmendment(activeId, aid))} onReject={(aid, reason) => run(() => rejectAmendment(activeId, aid, reason))} />

          {tab === "overview" && <OverviewTab data={data} bankTxns={bankTxns} canRecord={canRecord} onReconcile={(proposals) => run(() => addContributions(activeId, proposals))} onImport={() => setShowImport(true)} />}
          {tab === "members" && <MembersTab data={data} onOpenMember={openMemberDetail} myRole={myRole} requests={requests} canApprove={canApprove} onAddMember={() => setShowAddMember(true)} onInvite={() => setShowInvite(true)} onUpdateMember={(key, patch) => run(() => updateStokvelMember(activeId, key, patch))} onRemoveMember={(key, name) => { if (window.confirm("Remove " + name + " from the group?")) run(() => removeStokvelMember(activeId, key)); }} onSetPerms={(key, perms) => run(() => updateStokvelMember(activeId, key, { perms }))} onApprove={(uid, role, perms) => run(() => approveJoinRequest(activeId, uid, role, perms))} onReject={(uid, name) => { if (window.confirm("Decline " + (name || "this request") + "?")) run(() => rejectJoinRequest(activeId, uid)); }} />}
          {tab === "contributions" && <ContributionsTab data={data} onOpenMember={openMemberDetail} canRecord={canRecord} onAmend={(kind, entry) => setAmendTarget({ kind, entry })} onAdd={(c) => run(() => addContribution(activeId, c))} onAddAll={(list) => run(() => addContributions(activeId, list))} onVoid={(cid) => { if (window.confirm("Void this contribution?")) run(() => voidContribution(activeId, cid)); }} />}
          {tab === "loans" && <LoansTab data={data} onOpenMember={openMemberDetail} canRecord={canRecord} onAmend={(kind, entry) => setAmendTarget({ kind, entry })} onAddLoan={(l) => run(() => addLoan(activeId, l))} onAddRepayment={(r) => run(() => addRepayment(activeId, r))} onUpdateLoan={(lid, patch) => run(() => updateLoan(activeId, lid, patch))} />}
          {tab === "payouts" && <PayoutsTab data={data} onOpenMember={openMemberDetail} canRecord={canRecord} authUser={authUser} run={run} onAdd={(p) => run(() => addPayout(activeId, p))} onAddAll={(list) => run(async () => { for (const p of list) await addPayout(activeId, p); })} onDelete={(pid) => run(() => deletePayout(activeId, pid))} />}
          {tab === "claims" && <ClaimsTab data={data} canManageClaims={canManageClaims} canManageFamily={canManageFamily} myUid={myUid} activeId={activeId} run={run} />}
          {tab === "expenses" && <ExpensesTab data={data} canRecord={canRecord} authUser={authUser} run={run} onAddExpense={(e) => run(() => addExpense(activeId, e))} onDeleteExpense={(eid) => run(() => deleteExpense(activeId, eid))} onAddIncome={(i) => run(() => addIncome(activeId, i))} onDeleteIncome={(iid) => run(() => deleteIncome(activeId, iid))} />}
          {tab === "bankfeed" && <BankActivityTab data={data} bankTxns={bankTxns} canRecord={canRecord} bankLinked={bankLinked} syncing={bankSyncing} syncMsg={bankSyncMsg} onConnect={onConnectBank} onRefresh={onRefreshBank} />}
          {tab === "governance" && <GovernanceTab data={data} canGovern={canGovern} activeId={activeId} run={run} authUser={authUser} />}
          {tab === "permissions" && <PermissionsTab data={data} myRole={myRole} onUpdateMember={(key, patch) => run(() => updateStokvelMember(activeId, key, patch))} />}
          {tab === "settings" && <SettingsTab data={data} myRole={myRole} authUser={authUser} canManageSettings={canManageSettings} activeId={activeId} run={run} />}
        </div>
      ) : <div style={{ padding: 40, color: C.muted }}>Loading group…</div>}

      {memberDetail && data && <MemberDetailView member={memberDetail.member} focus={memberDetail.focus} data={data} onClose={() => setMemberDetail(null)} />}

      {showCreate && <CreateStokvelModal onClose={() => setShowCreate(false)} onCreate={(d) => { setShowCreate(false); run(async () => { const id = await createStokvel(d, authUser); setActiveId(id); }); }} />}
      {showJoin && <JoinStokvelModal onClose={() => setShowJoin(false)} onJoined={(msg) => { setShowJoin(false); setJoinMsg(msg); refreshList(); }} />}
      {amendTarget && <AmendModal target={amendTarget} onClose={() => setAmendTarget(null)} onSubmit={(payload) => { setAmendTarget(null); run(() => proposeAmendment(activeId, payload)); }} />}
      {showAddMember && data && <AddMemberModal stokvel={data.stokvel} onClose={() => setShowAddMember(false)} onAdd={(m) => { setShowAddMember(false); run(() => addStokvelMember(activeId, m)); }} />}
      {showInvite && data && <InviteModal stokvel={data.stokvel} onClose={() => setShowInvite(false)} onSetCode={async (code, active) => { await setStokvelJoinCode(activeId, code, active); await reload(); }} />}
      {showImport && data && <ImportStatementModal stokvel={data.stokvel} data={data} onClose={() => setShowImport(false)} onConfirm={(buckets) => {
        setShowImport(false);
        run(() => applyImport(activeId, buckets));
      }} />}
    </div>
  );
}

export default StokvelView;
